/// <amd-module name="Wit/Common/BacklogContentService/ICommonBacklogContentService" />
import * as React from "react";
import { IBacklogConfiguration } from "Agile/Common/Redux/BacklogConfiguration/BacklogConfigurationContracts";
import { ITeamFieldData, ITeamSettings } from "Agile/Contracts/Common";
import { ITfsPageService, getTFSProject } from "Tfs/Platform/Page";
import { IObservableValue, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { equals, format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssLayoutManager, WrappedComponent } from "VSS/Platform/Layout";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { ListSelection } from "VSSUI/List";
import { IMenuItem, MenuItemType } from "VSSUI/Menu";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { Filter, IFilter } from "VSSUI/Utilities/Filter";
import { ITreeItem, ITreeItemAdd, ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { RollupProgressBarData, WorkItemRollupProvider } from "Wit/AgileAnalytics/WorkItemRollupProvider";
import { WorkItemTypeColorAndIcon } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { BacklogLevelConfiguration, BacklogType } from "Wit/Clients/Work/Contracts/Work";
import { getWorkClient } from "Wit/Clients/Work/RestClient/Work";
import { BacklogColumnSettings, CommonBacklogDataProviders } from "Wit/Common/BacklogContentService/BacklogContentContants";
import {
    DisplayState,
    HubType,
    IBacklogItem,
    IBacklogTree,
    IBacklogTreeRow,
    ObservableBacklogItem
} from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import * as Resources from "Wit/Common/BacklogContentService/Resources";
import { BulkUpdateConstants, IWorkItemFieldChange } from "Wit/Common/BulkUpdateUtils/BulkUpdateInterfaces";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { ICurrentDirectoryService } from "Wit/Common/CurrentDirectoryService/ICurrentDirectoryService";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { ExceptionInfo, ExceptionInfoResult } from "Wit/Common/HubError/Models/Models";
import { QuerySaveDialog } from "Wit/Common/QuerySaveDialog/Components/QuerySaveDialog";
import { QueryItem, QuerySaveDialogMode } from "Wit/Common/QueryUtils/Models";
import { getQueryUrl } from "Wit/Common/QueryUtils/RoutingUtils";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import * as WorkItemUtils from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { bulkUpdateWorkItems } from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { IWorkItemFieldService, getWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2, getWorkItemFieldService as getWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { IWorkItemImportIdentityService, WorkItemIdentityServiceName } from "Wit/Common/WorkItemIdentityService/IWorkItemIdentityService";
import { IWorkItemTypesService } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import { DirectoryItem } from "Wit/DirectoryService/Client/Contracts/Directory";
import { getAreaPathLeafNode } from "Wit/Filter/AreaPathHelper";
import { FilterManager, FilterValue, IBoardFilterDataSource, IParentFilterItem, isFilterStateEmpty } from "Wit/Filter/FilterManager";
import { getUniqueValuesForAssignedTo } from "Wit/Filter/Utilities/Utilities";
import { mapIFilterState, mapToFilterState } from "Wit/Filter/WorkItemFilter";
import { convertWorkItemIdentityRefFromFieldValue, isWorkItemIdentityRef, parseUniquefiedIdentityName } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { IBoardsMessagesService, getBoardsMessagesService, getErrorMessage } from "Wit/Messages/BoardsMessagesService";
import { IWorkItemStateColorMetadataService } from "Wit/UI/Services/WorkItemStateColorMetadataService";
import { IWorkItemTypeIconMetadataService } from "Wit/UI/Services/WorkItemTypeIconMetadataService";
import { WorkItemStateAndColor } from "Wit/UI/WorkItemState";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItem, IWorkItemBulkSaveResult, IWorkItemSavingFailedError } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { IWorkItemSavingService } from "Wit/WorkItemModel/Services/WorkItemSavingService";
import {
    BacklogQueryResultType,
    IBacklogColumn,
    IBacklogQueryResults,
    IColumnPreference,
    ICommonBacklogData,
    IPageWorkItemsDataProviderResponse,
    ITeam,
    RawRowData
} from "./BacklogContentDataProviderTypes";
import { IBacklogWorkItemConfigurationService } from "./BacklogWorkItemConfigurationService";
import { IHubContentService } from "./IHubContentService";
import {
    BacklogFieldTypes,
    IRollupColumn,
    RollupAggregation,
    RollupCalculationSettings,
    RollupType,
    getRollupColumnDisplayName,
    getRollupColumnReferenceName,
    isRollupEqual
} from "./RollupHelpers";

export interface ICommonBacklogContentService extends IVssService, IBoardFilterDataSource {
    addRowToIdsMap(newRow: IBacklogTreeRow): void;
    clearErrorMessage(): void;
    createNewTreeItem(
        id: number,
        columnMapDict: { [columnId: string]: number },
        coreColumns: { [columnId: string]: string | number | null },
        witType: string,
        url?: string,
        order?: number,
        parent?: IBacklogTreeRow
    ): Promise<ITreeItem<ObservableBacklogItem>>;
    filterWorkItems: (ids: number[], flatFilter: boolean, keepHierarchyWithFilters?: boolean) => void;
    getAllBacklogLevels(teamId?: string): Promise<BacklogLevelConfiguration[]>;
    getWorkItemTypesOfBacklogLevels(levelIds: string[]): Promise<Set<string>>;
    getAllVisibleLevels(): BacklogLevelConfiguration[];
    getAvailableRollupColumns(currentBacklogLevel: IObservableValue<string>): Promise<IRollupColumn[]>;
    getRollupColumnNameAlignedWithBacklog(
        rollupMetric: RollupCalculationSettings,
        friendlyNames?: { backlogName?: string; fieldName?: string }
    ): string;
    getBacklogCommandBarItems(hubService: IHubContentService): IHeaderCommandBarItem[];
    getBacklogConfiguration(ignoreCache?: boolean): Promise<IBacklogConfiguration | undefined>;
    getBacklogConfigurationSync(): IBacklogConfiguration | undefined;
    getBacklogLevelId(): string;
    getBacklogTree(
        getData?: (options: any) => Promise<ICommonBacklogData | ExceptionInfoResult>,
        options?: { updateFiltersOrOrder: boolean; ignoreCache: boolean }
    ): IObservableValue<IBacklogTree | undefined>;
    getBacklogWiql(
        getData?: (options: any) => Promise<ICommonBacklogData | ExceptionInfoResult>,
        options?: { ignoreCache: boolean }
    ): IReadonlyObservableValue<string>;
    getAllIdsToRowsMap(): Map<number, IBacklogTreeRow>;
    getCurrentTeam: () => ITeam;
    getDisplayState(): IReadonlyObservableValue<DisplayState>;
    getExceptionInfo(): IReadonlyObservableValue<ExceptionInfo | undefined>;
    getFilter: () => IFilter;
    getIsRequirementBacklog(): boolean;
    getIsRootBacklog(): boolean;
    getNewItemCalloutAnchor(): ObservableValue<HTMLElement | undefined>;
    /**
     * Get or create an unparented folder for the current backlog level
     * If necessary unparented folders will be created for every backlog levels until the needed one is reached
     *
     * @returns an unparented folder for the current backlog level, undefined if it's the root level
     */
    getOrAddUnparentedFolder(): ITreeItem<IBacklogTreeRow> | undefined;
    getPageContext(): IVssPageContext;
    getParentWorkItems(): Map<number, ObservableValue<IWorkItem | undefined>>;
    getQueryType(): BacklogQueryResultType | undefined;
    getParentWorkItem: (parentId: number) => ObservableValue<IWorkItem | undefined> | undefined;
    getRecentQueryUrl(): string;
    getRollupData(id: number, rollupCalculation: RollupCalculationSettings): IObservableValue<RollupProgressBarData | undefined>;
    getSelectedItems(item?: IBacklogTreeRow): IBacklogTreeRow[];
    getSelectedItemsIdFromRawBacklogTree(fetchChildren: boolean, useFetchChildrenOptimization: boolean): Promise<number[]>;
    getSelectedProviderItems(item?: ITreeItemEx<IBacklogTreeRow>): ITreeItemEx<IBacklogTreeRow>[];
    getSelectedProviderItemsWithChildren(item?: ITreeItemEx<IBacklogTreeRow>, checkAllowedForBacklogTypes?: boolean): Promise<ITreeItemEx<IBacklogTreeRow>[]>
    getSelectedTreeItems(item?: ITreeItem<IBacklogTreeRow>): ITreeItem<IBacklogTreeRow>[];
    getShouldShowOrderValues(): boolean;
    getShowFilterHeader(): ObservableValue<boolean>;
    getShowQueryHeader(): ObservableValue<boolean>;
    getTeamAreaNodes(): Map<string, ITeamFieldData>;
    getTeamField(): string | undefined;
    getTreeSelection(): ListSelection;
    getBacklogWorkItemTypeNames(): string[];
    getBacklogDefaultWorkItemTypeName(teamId?: string, bagklogLevelId?: string): Promise<string | undefined>;
    getWorkItemIdsSet(): Set<number>;
    insertItems(
        selectedProviderItems: ITreeItemEx<IBacklogTreeRow>[],
        insertAfterTreeProviderItem?: ITreeItemEx<IBacklogTreeRow>,
        insertIndex?: number
    ): void;
    isParentInSameProject(workItemId: number, parentId: number): boolean;
    processFetchedWorkItems(
        response: IPageWorkItemsDataProviderResponse,
        workItemStateAndColorDict: { [workItemType: string]: WorkItemStateAndColor[] },
        workItemTypeColorAndIcons: WorkItemTypeColorAndIcon[]
    ): Promise<void>;
    refreshTree(options?: {
        getData?: (options: any) => Promise<ICommonBacklogData | ExceptionInfoResult>;
        keepOpen?: boolean;
        filterManager?: FilterManager;
    }): Promise<void>;
    reorderBacklogTreeRows(selectedProviderItems: ITreeItemEx<IBacklogTreeRow>[], insertAfterItem: ITreeItem<IBacklogTreeRow> | undefined): void;
    resetOrderColumn(suspendNotifications?: boolean): void;
    selectAllItems(): void;
    setDisplayState(displayState: DisplayState): void;
    setExceptionInfo(exceptionInfo: ExceptionInfo | undefined): void;
    setHubType(hubType: HubType): void;
    setNumberFilteredItems(itemsPaged: number): void;
    setShouldShowOrderValues(showOrder: boolean): void;
    settingsServiceResizeColumn(settingKey: string, backlogColumnId: number, newWidth: number, levelKey?: string): Promise<void>;
    settingsServiceSetColumns(settingKey: string, backlogColumns: IBacklogColumn[], levelKey?: string): Promise<void>;
    settingsServiceGetColumns(settingKey: string, levelKey?: string): Promise<IBacklogColumn[]>;
    showBulkSaveErrorMessage(error: IWorkItemSavingFailedError, severity?: MessageCardSeverity): void;
    showErrorMessage(error: any, severity?: MessageCardSeverity): void;
    teamOwnsWorkItem(areaPath: string): boolean;
    /**
     * Updates and reorders tree for specific work items
     *
     * @param workItems - changed work items.
     * @param updateColumns - method thats changed specific columns inside treeItem
     * @param needUpdateChildNodes - a flag that indicates what we need change specific columns inside child treeItems (if treerow is not expanded)
     * @param removeCondition - a method that indicates whether child elements should be removed  (if treerow is not expanded)
     */
    updateTreeLocally(
        workItems: IWorkItem[],
        updateColumns: (itemValue: IBacklogItem) => void,
        needUpdateChildNodes?: boolean,
        removeCondition?: (workItem: IWorkItem, itemValue: IBacklogItem) => boolean,
        updateBacklogItemCoreColumnDataForChildren?: boolean
    ): Promise<void>;
    updateWorkItemRow(workItemId: number, workItem?: IWorkItem, forceRefresh?: boolean): Promise<void>;
    updateWorkItemRows(rows: { workItemId: number; workItem?: IWorkItem; forceRefresh?: boolean }[]): Promise<void>;
    /**
     * Checks that workItem is in Complete state
     *
     * @param workItem - work item thats need to be check
     */
    checkCompletedStateAsync(workItem: IBacklogTreeRow): Promise<boolean>;

    updateIterationCommonLogic(
        currentIterationData: ICurrentIterationData,
        workItems: IChangeIterationWorkItemData[],
        filterManager: FilterManager,
        processReorderCallback?: (workItemIds: number[]) => Promise<void>,
        useChangeIterationPerfomanceOptimization?: boolean,
        needCleanItemsFromTree?: boolean
    ): Promise<{ changeIterationIsSuccessful: boolean, numberOfSavedWorkitems: number }>;

    getWorkitemTypesMinDepth(workItemTypes: string[]): number;
    updateDisplayState(): void;
    getIsLoadingNextBatchObservable(): IObservableValue<boolean>;

    setBulkBacklogOperationIsInProgress(bulkEditMarker: boolean): void;
    isBulkBacklogOperationInProgress(): boolean;
}

export interface IChangeIterationWorkItemData {
    workItemId: number;
    workItemIterationPath: string;
    workItemIterationId: number;
    workItemTypeName: string;
    workItemProjectName: string;
}

export interface ICurrentIterationData {
    path: string | undefined;
    newIterationPath: string | undefined;
    newNodeId: number | undefined;
    iterationId: string | undefined;
    iterationName: string | undefined;
}

/** Must be kept in sync with constant in BacklogContentView.tsx */
const unparentedItemId = -1;

class CommonBacklogContentService extends VssService implements ICommonBacklogContentService {
    private allIdsToRowsMap = new Map<number, IBacklogTreeRow>();
    private allBacklogLevels: BacklogLevelConfiguration[] = [];
    private allBacklogLevelsPerTeam: Map<string, BacklogLevelConfiguration[]> = new Map();
    private backlogContent: IBacklogQueryResults | undefined;
    private backlogLevelId: string;
    private backlogTree = new ObservableValue<IBacklogTree | undefined>(undefined);
    private backlogWiql: ObservableValue<string> = new ObservableValue("");
    private backlogWorkItemTypeNames: string[] = [];
    private buildBacklogTreePromise?: Promise<IBacklogTree | undefined>;
    private columnPreferences: IColumnPreference[];
    private currentTeam: ITeam;
    private displayState = new ObservableValue<DisplayState>(DisplayState.Loading);
    private exceptionInfo = new ObservableValue<ExceptionInfo | undefined>(undefined);
    private filter = new Filter();
    private filteredIds: number[] = [];
    private workItemFieldService: IWorkItemFieldService | IWorkItemFieldService2;
    private messagesService: IBoardsMessagesService;
    private treeItemProvider: IObservableValue<TreeItemProvider<IBacklogTreeRow> | undefined>;
    private treeSelection = new ListSelection(true);

    private teamSettings: IReadonlyObservableValue<ITeamSettings | undefined>;
    private queryUrl: string;
    private teamAreaNodesById: Map<string, ITeamFieldData> = new Map();

    private parentWorkItems: Map<number, ObservableValue<IWorkItem | undefined>> = new Map();

    private isRequirementsBacklog = new ObservableValue(false);
    private isRootBacklog = new ObservableValue(false);

    private rollupProviders: WorkItemRollupProvider[];
    private newItemCalloutAnchor = new ObservableValue<HTMLElement | undefined>(undefined);

    private filteredItems: number;
    private showFilterHeader: ObservableValue<boolean> = new ObservableValue(false);
    private showQueryHeader: ObservableValue<boolean> = new ObservableValue(false);
    private shouldShowOrderValues: boolean = true;
    private workItemIdsSet: Set<number> | undefined;
    private hub: HubType = HubType.Backlogs;
    private backlogConfiguration: IBacklogConfiguration | undefined;
    private historyService: IVssHistoryService;

    private teamSettingsService: ITeamSettingsService;
    private backlogTreeLoadingOptimizationIsEnabled: boolean;
    private backlogLevelKeyFixEnabled: boolean;
    private backlogDelayLoadIdentitiesIsEnabled: boolean;
    private newBacklogRerenderingOnFilterBehavior: boolean;
    private _isLoadingNextBatch = new ObservableValue(false);
    private bulkBacklogOperationIsInProgress: boolean;

    public _serviceStart = (pageContext: IVssPageContext): void => {
        super._serviceStart(pageContext);

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const currentTeam = contributionService.getData<ITeam | undefined>(CommonBacklogDataProviders.CurrentTeam);
        this.teamSettingsService = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");

        if (currentTeam && currentTeam.name) {
            this.currentTeam = currentTeam;
            this.teamSettingsService.updateTeamSettings({}, this.currentTeam.id);
            this.teamSettings = this.teamSettingsService.getTeamSettingsObservable({ ignoreCache: false, teamId: this.currentTeam.id });
        }

        this.messagesService = getBoardsMessagesService(pageContext);
        this.workItemFieldService = isFeatureFlagEnabled(this.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)
            ? getWorkItemFieldService2(this.pageContext)
            : getWorkItemFieldService(this.pageContext);
        this.historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this.backlogTreeLoadingOptimizationIsEnabled = isFeatureFlagEnabled(
            pageContext,
            FeatureFlags.BacklogTreeLoadingOptimizationIsEnabled,
            false);
        this.backlogDelayLoadIdentitiesIsEnabled = isFeatureFlagEnabled(
            pageContext,
            FeatureFlags.BacklogDelayLoadIdentities,
            false);
        this.backlogLevelKeyFixEnabled = !isFeatureFlagEnabled(pageContext, FeatureFlags.BacklogLevelKeyFixDisabled, false);
        this.newBacklogRerenderingOnFilterBehavior = isFeatureFlagEnabled(pageContext, FeatureFlags.LoadingSpinnerOnFilterChangeEnabled, false);
    };

    public _serviceEnd = (pageContext: IVssPageContext): void => {
        super._serviceEnd(pageContext);
    };

    public setBulkBacklogOperationIsInProgress(bulkEditMarker: boolean): void {
        this.bulkBacklogOperationIsInProgress = bulkEditMarker;
    }

    public isBulkBacklogOperationInProgress(): boolean {
        return this.bulkBacklogOperationIsInProgress;
    }

    public addRowToIdsMap = (newRow: IBacklogTreeRow) => {
        this.allIdsToRowsMap.set(newRow.id, newRow);
    };

    public clearErrorMessage = () => {
        this.messagesService.dismissMessage();
    };

    private getWorkItemFromModelService = async (workItemIds: number[], isCacheDisabled?: boolean): Promise<IWorkItem[]> => {
        const workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        return workItemModelService.getWorkItems(workItemIds, undefined, undefined, undefined, isCacheDisabled);
    };

    public async updateIterationCommonLogic(
        currentIterationData: ICurrentIterationData,
        workItems: IChangeIterationWorkItemData[],
        filterManager: FilterManager,
        processReorderCallback?: (workItemIds: number[]) => Promise<void>,
        useChangeIterationPerfomanceOptimization?: boolean,
        needCleanItemsFromTree?: boolean
    ): Promise<{ changeIterationIsSuccessful: boolean, numberOfSavedWorkitems: number }> {

        if (currentIterationData.path && currentIterationData.newIterationPath) {

            const workItemIds: number[] = workItems.map(workItem => workItem.workItemId);
            await processReorderCallback?.(workItemIds);

            const { changeIterationIsSuccessful, numberOfSavedWorkitems } = await this.changeWorkItemIteration(
                workItems,
                filterManager,
                currentIterationData.newNodeId,
                useChangeIterationPerfomanceOptimization ? currentIterationData.newIterationPath : currentIterationData.path,
                useChangeIterationPerfomanceOptimization && needCleanItemsFromTree /* needCleanItemsFromTree */,
                useChangeIterationPerfomanceOptimization
            );

            return { changeIterationIsSuccessful, numberOfSavedWorkitems };
        }

        return { changeIterationIsSuccessful: false, numberOfSavedWorkitems: 0 };
    }

    private changeWorkItemIteration = async (
        workItemsData: IChangeIterationWorkItemData[],
        filterManager: FilterManager,
        iterationId: number | undefined,
        newIterationPath: string,
        needCleanItemsFromTree?: boolean,
        useChangeIterationPerfomanceOptimization?: boolean
    ): Promise<{ changeIterationIsSuccessful: boolean, numberOfSavedWorkitems: number }> => {
        const currentProject = this.pageContext.getService<ITfsPageService>("ITfsPageService").getData()?.project;
        const bulkErrorMessages: string[] = [];
        const workItemsToSaveData: IChangeIterationWorkItemData[] = [];
        workItemsData.forEach(workItemData => {
            if (currentProject?.name !== workItemData.workItemProjectName) {
                bulkErrorMessages.push(Resources.ChangeIterationPathError);
            } else {
                workItemsToSaveData.push(workItemData);
            }
        });

        const changeIterationIdAndPathColumns = (itemValue: IBacklogItem): void => {
            if (iterationId) {
                itemValue.coreColumnMap[CoreFieldRefNames.IterationId] = iterationId;
            }
            itemValue.coreColumnMap[CoreFieldRefNames.IterationPath] = newIterationPath;
        }

        const previousIterationPath: string = workItemsToSaveData?.length ? workItemsToSaveData[0].workItemIterationPath : "";

        const checkThatTreeRowCanBeRemoved = (workItem: IWorkItem, itemValue: IBacklogItem): boolean => {
            const useDeleteCheck: boolean = needCleanItemsFromTree != undefined && needCleanItemsFromTree === true;
            const currentIterationPath = itemValue.coreColumnMap[CoreFieldRefNames.IterationPath] as string;
            const id = itemValue.coreColumnMap[CoreFieldRefNames.Id] as number;
            const idIsMatch = id ? workItem.id === id : false;
            const result = useDeleteCheck && idIsMatch && previousIterationPath != currentIterationPath;
            return result;
        }

        let saveOperationIsSuccessful = true;
        const workItemIds = workItemsToSaveData.map(workItem => workItem.workItemId);

        if (useChangeIterationPerfomanceOptimization && workItemsToSaveData.length) {
            const changesMap = new Map<string, IWorkItemFieldChange[]>();
            const changesList: IWorkItemFieldChange[] = [];

            if (iterationId) {
                changesList.push({ fieldName: CoreFieldRefNames.IterationId, value: iterationId });
            }
            changesList.push({ fieldName: CoreFieldRefNames.IterationPath, value: newIterationPath });
            changesMap.set(BulkUpdateConstants.AllTypes, changesList);

            try {
                this.setBulkBacklogOperationIsInProgress(true);
                await bulkUpdateWorkItems(
                    this.pageContext,
                    workItemIds,
                    changesMap,
                    {
                        selection: this.getTreeSelection(),
                        useProcessDialog: true,
                        onModifyItems: async workItems => {
                            const batchSaveOperationIsSuccessful = await this.saveWorkItems(workItems, filterManager, useChangeIterationPerfomanceOptimization);
                            saveOperationIsSuccessful = saveOperationIsSuccessful && batchSaveOperationIsSuccessful;
                            await this.updateTreeLocally(
                                workItems,
                                changeIterationIdAndPathColumns,
                                true,
                                checkThatTreeRowCanBeRemoved,
                                useChangeIterationPerfomanceOptimization
                            );
                        },
                    })
                if (bulkErrorMessages.length) {
                    this.showBulkSaveErrorMessages(bulkErrorMessages);
                }
            } finally {
                this.setBulkBacklogOperationIsInProgress(false);
            }

            return { changeIterationIsSuccessful: saveOperationIsSuccessful, numberOfSavedWorkitems: workItemsToSaveData.length };
        }

        const workItems: IWorkItem[] = await this.getWorkItemFromModelService(workItemIds);
        const workItemsToSave: IWorkItem[] = [];
        const bulkErrorSaveResults: IWorkItemBulkSaveResult[] = [];

        if (workItems.length) {
            workItems.map(workItem => {
                if (currentProject?.id !== workItem.project.guid) {
                    bulkErrorSaveResults.push({
                        workItem: workItem,
                        error: {
                            message: Resources.ChangeIterationPathError
                        }
                    });
                } else {
                    iterationId && workItem.setFieldValueByName(CoreFieldRefNames.IterationId, iterationId);
                    workItem.setFieldValueByName(CoreFieldRefNames.IterationPath, newIterationPath);
                    workItemsToSave.push(workItem);
                }
            });
        }

        if (workItems.length > 0) {
            await this.updateTreeLocally(
                workItemsToSave,
                changeIterationIdAndPathColumns,
                true,
                checkThatTreeRowCanBeRemoved
            );
        }

        saveOperationIsSuccessful = await this.saveWorkItems(workItemsToSave, filterManager);
        if (bulkErrorSaveResults.length) {
            const error: IWorkItemSavingFailedError = {
                results: bulkErrorSaveResults,
                name: Resources.BulkSaveErrorMessage,
                message: Resources.BulkSaveErrorMessage
            };
            this.showBulkSaveErrorMessage(error);
        } 

        return { changeIterationIsSuccessful: saveOperationIsSuccessful, numberOfSavedWorkitems: workItemsToSave.length };
    };

    private saveWorkItems = async (workItems: IWorkItem[], filterManager: FilterManager, skipUnchangedItems?: boolean): Promise<boolean> => {
        const savingService = this.pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");
        try {
            await savingService.saveWorkItemsBatchAsync(workItems);
            this.clearErrorMessage();
        } catch (error) {
            this.updateIsValid(workItems.map(wi => ({ workItem: wi, isValid: false })), skipUnchangedItems);
            this.showBulkSaveErrorMessage(error);
            return false;
        }

        this.updateIsValid(workItems.map(wi => ({ workItem: wi, isValid: true })), skipUnchangedItems);
        filterManager.dataUpdated();
        return true;
    };

    private updateIsValid(update: { workItem: IWorkItem; isValid: boolean }[], skipUnchangedItems?: boolean) {
        const backlogTree = this.getBacklogTree();
        const updatesToUse: { workItemId: number; workItem?: IWorkItem; forceRefresh?: boolean }[] = [];

        if (backlogTree.value) {
            for (const el of update) {
                const itemProvider = backlogTree.value.itemProvider.value;
                const item = itemProvider.find(elem => elem.underlyingItem.data.id === el.workItem.id)?.underlyingItem.data.value;
                if (item) {
                    if (skipUnchangedItems && item.isValid === el.isValid) {
                        continue;
                    }
                    item.isValid = el.isValid;
                }

                updatesToUse.push({ workItemId: el.workItem.id, workItem: el.workItem });
            }
        }

        if (updatesToUse.length > 0) {
            this.updateWorkItemRows(updatesToUse);
        }
    }

    public async checkCompletedStateAsync(workItem: IBacklogTreeRow): Promise<boolean> {
        const backlogConfiguration = await this.getBacklogConfiguration();

        if (!backlogConfiguration) {
            return false;
        }

        const stateValue = workItem.value.coreColumnMap[CoreFieldRefNames.State] as string;
        const workItemType = workItem.value.coreColumnMap[CoreFieldRefNames.WorkItemType] as string;

        const workItemTypesService = this.pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
        return workItemTypesService.checkCompletedState(stateValue, workItemType, backlogConfiguration);
    }

    public async createNewTreeItem(
        id: number,
        columnMapDict: { [columnId: string]: number },
        coreColumns: { [columnId: string]: string | number | null },
        witType: string,
        url?: string,
        order?: number,
        parent?: IBacklogTreeRow
    ) {
        const workItemConfigurationService = this.pageContext.getService<IBacklogWorkItemConfigurationService>(
            "IBacklogWorkItemConfigurationService"
        );
        const colorAndIconMap = await workItemConfigurationService.getWorkItemTypeColorAndIcons();
        const colorAndIcon = colorAndIconMap.find(colorAndIcons => colorAndIcons.workItemTypeName === witType);
        const workItemStateAndColor = await workItemConfigurationService.getWorkItemStateAndColorDictionary();

        let columnValues = Object.entries(columnMapDict)
            .sort((a, b) => a[1] - b[1])
            .map(entry => coreColumns[entry[0]] ?? null) as RawRowData[];

        const childWorkItemType = await this.getBacklogChildrenFromType(witType, colorAndIconMap);
        columnValues = this.updateRollupDataForNewItem(id, columnValues);

        return {
            data: new ObservableBacklogItem(id, parent, {
                childWorkItemTypes: childWorkItemType,
                coreColumnMap: coreColumns,
                columnValues: columnValues,
                order: order,
                isValid: true,
                workItemUrl: url ?? "",
                workItemStateAndColor: workItemStateAndColor[witType][0], // New work item will be first available state [0]  for the work item type
                workItemTypeColorAndIcon: colorAndIcon
            })
        };
    }

    public filterWorkItems(ids: number[], flat: boolean, keepHierarchyWithFilters?: boolean) {
        this.filteredIds = ids;

        if (!this.backlogTree.value || !this.backlogTree.value.allRows) {
            return;
        }

        let finalItems: ITreeItem<IBacklogTreeRow>[] | undefined = undefined;

        if (keepHierarchyWithFilters && keepHierarchyWithFilters === true) {
            finalItems = this.hierarchyFilterBehaviour(ids, flat);
        } else {
            finalItems = this.flatFilterBehaviour(ids, flat);
        }

        if (this.backlogTree.value) {
            const selectedItems = this.getSelectedItems();

            const itemProvider = new TreeItemProvider(finalItems);

            if (selectedItems) {
                const treeSelection = this.getTreeSelection();
                treeSelection.clear();

                for (const selectedItem of selectedItems) {
                    const index = itemProvider?.value.findIndex(item => item.underlyingItem.data.id === selectedItem.id);

                    if (index !== undefined && index !== -1) {
                        treeSelection.select(index, 1, true);
                    }
                }
            }

            this.backlogTree.value = { ...this.backlogTree.value, itemProvider: itemProvider };
        }
    }

    private hierarchyFilterBehaviour(ids: number[], filtertIsSet: boolean) {
        if (!this.backlogTree.value || !this.backlogTree.value.allRows) {
            return;
        }

        let matchedParents: ITreeItem<IBacklogTreeRow>[] | undefined = undefined;

        if (filtertIsSet) {
            let rootNodes: ITreeItem<IBacklogTreeRow>[] = [];

            this.backlogTree.value.rows.forEach(row => {
                if (row.data) {
                    // If current parent node have required id OR
                    // If any children have required id
                    if (ids.some(id => row.data.id === id) || (row.childItems && this.anyChildContainsId(row.childItems, ids))) {
                        rootNodes.push(row);
                    }
                }
            });

            matchedParents = this.madeFilteredTree(rootNodes, ids, true);
        } else {
            matchedParents = this.backlogTree.value.rows;
        }

        return matchedParents;
    }

    private madeFilteredTree(matchedParents: ITreeItem<IBacklogTreeRow>[] | undefined, ids: number[], firstIteration: boolean = false) {
        if (!matchedParents) {
            return undefined;
        }

        let parentList: ITreeItem<IBacklogTreeRow>[] | undefined = firstIteration ? [] : undefined;

        for (let parentNode of matchedParents) {
            let needAddToList = false;
            let expanded = false;

            if (!parentNode) {
                continue;
            }

            if (ids.find(id => parentNode.data.id === id)) {
                needAddToList = true;
            }

            if (parentNode.childItems && this.anyChildContainsId(parentNode.childItems, ids)) {
                parentNode.expanded = true;
                expanded = true;
                needAddToList = true;
            }

            if (needAddToList || firstIteration) {
                if (!parentList) {
                    parentList = [];
                }

                const parentNodeCopy = this.makeCopy(parentNode);
                parentNodeCopy.highlighted = false;
                parentNodeCopy.expanded = expanded;
                parentNodeCopy.childItems = this.madeFilteredTree(parentNode.childItems, ids);

                parentList.push(parentNodeCopy);
            }
        }

        return parentList;
    }

    private makeCopy(row: ITreeItem<IBacklogTreeRow>): ITreeItem<IBacklogTreeRow> {
        return {
            childItems: undefined,
            data: row.data,
            expanded: row.expanded,
            id: row.id,
            text: row.text
        };
    }

    private anyChildContainsId(childItems: ITreeItem<IBacklogTreeRow>[], ids: number[]): boolean {
        for (let childItem of childItems) {
            if (childItem.data) {
                if (ids.some(id => childItem.data.id === id)) {
                    return true;
                }
            }

            if (childItem.childItems) {
                if (this.anyChildContainsId(childItem.childItems, ids)) {
                    return true;
                }
            }
        }

        return false;
    }

    private getRowsAccordingToOrder(filteredRows: IBacklogTreeRow[]) {
        if (!this.backlogTree.value?.rows) {
            return undefined;
        }

        const idsRowsMap: { [id: string]: IBacklogTreeRow } = {};
        const isAdded: { [id: string]: boolean } = {};
        const orderedAndFilteredRows: IBacklogTreeRow[] = [];
        const totalNumberOfFilteredRows = filteredRows.length;

        filteredRows.forEach(row => {
            idsRowsMap[row.id] = row;
        });

        const matchRows = (rows?: ITreeItem<IBacklogTreeRow>[]) => {
            if (!rows) {
                return;
            }

            for (let i = 0; i < rows.length; i++) {
                const id = rows[i].data.id;
                if (id >= 0 && idsRowsMap[id] && !isAdded[id]) {
                    isAdded[id] = true;
                    orderedAndFilteredRows.push(rows[i].data);

                    if (orderedAndFilteredRows.length === totalNumberOfFilteredRows) {
                        return;
                    }
                }

                matchRows(rows[i].childItems);
                if (orderedAndFilteredRows.length === totalNumberOfFilteredRows) {
                    return;
                }
            }
        };

        matchRows(this.backlogTree.value.rows);
        return orderedAndFilteredRows;
    }

    private flatFilterBehaviour(ids: number[], flat: boolean) {
        let filteredRows = [];
        for (const id of ids) {
            const row = this.allIdsToRowsMap.get(Number(id));
            if (row) {
                filteredRows.push(row);
            }
        }

        if (this.allIdsToRowsMap.keys.length > this.filteredItems) {
            this.showFilterHeader.value = true;
        }

        if (!this.backlogTree.value || !this.backlogTree.value.allRows) {
            return;
        }

        filteredRows = this.getRowsAccordingToOrder(filteredRows) ?? filteredRows;

        // This will be "flat" if we are filtering & "not flat" if we want to clear the filter
        const finalItems: ITreeItem<IBacklogTreeRow>[] = flat ? filteredRows.map(item => ({ data: item })) : this.backlogTree.value.rows;

        return finalItems;
    }

    public async getAllBacklogLevels(teamId?: string): Promise<BacklogLevelConfiguration[]> {
        if (teamId) {
            const existingRecord = this.allBacklogLevelsPerTeam.get(teamId);
            if (existingRecord) {
                return existingRecord;
            } else {
                const workClient = getWorkClient(this.pageContext);
                const project = getTFSProject(this.pageContext);

                const backlogLevelsForTeam = (
                    await workClient.getBacklogs({
                        project: project?.name ?? "",
                        projectId: project?.id!,
                        team: "",
                        teamId: teamId
                    })
                ).sort((a, b) => b.rank - a.rank);
                this.allBacklogLevelsPerTeam.set(teamId, backlogLevelsForTeam);
                return backlogLevelsForTeam;
            }
        } else {
            if (!this.allBacklogLevels || this.allBacklogLevels.length === 0) {
                const workClient = getWorkClient(this.pageContext);
                const project = getTFSProject(this.pageContext);

                const backlogLevels = await workClient.getBacklogs({
                    project: project?.name ?? "",
                    projectId: project?.id!,
                    team: "",
                    teamId: this.currentTeam.id
                });
                this.allBacklogLevels = backlogLevels.sort((a, b) => b.rank - a.rank);
            }

            return this.allBacklogLevels;
        }
    }

    public async getWorkItemTypesOfBacklogLevels(levelIds: string[]): Promise<Set<string>> {
        const allBacklogLevels = await this.getAllBacklogLevels();
        const backlogLevels = allBacklogLevels.filter(level => levelIds.some(x => x === level.id));
        const workItemTypeReferences = backlogLevels.reduce((workItemTypeReferences, level) => [...workItemTypeReferences, ...level.workItemTypes], []);
        return new Set(workItemTypeReferences.map(wt => wt.name));
    }

    public async getAvailableRollupColumns(currentBacklogLevel: IObservableValue<string>): Promise<IRollupColumn[]> {
        if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)) {
            await (this.workItemFieldService as IWorkItemFieldService2).loadFields();
        } else {
            await (this.workItemFieldService as IWorkItemFieldService).getFieldsAsync();
        }

        const backlogConfiguration = await this.getBacklogConfiguration();

        const rollupProgressMetric = {
            type: RollupType.Progress,
            aggregation: RollupAggregation.Count
        };
        const rollupTotalMetric = {
            type: RollupType.Total,
            aggregation: RollupAggregation.Count
        };

        const rollupTypeToAvailableRollupColumns: { [rollupType: number]: IRollupColumn[] } = {
            [RollupType.Progress]: [
                {
                    displayName: Resources.ProgressByCompletedDescendents,
                    referenceName: getRollupColumnReferenceName(rollupProgressMetric),
                    rollupCalculation: rollupProgressMetric
                }
            ],
            [RollupType.Total]: [
                {
                    displayName: Resources.TotalByAllDescendents,
                    referenceName: getRollupColumnReferenceName(rollupTotalMetric),
                    rollupCalculation: rollupTotalMetric
                }
            ]
        };

        const rollupTypes = [RollupType.Progress, RollupType.Total];

        const allBacklogLevels = await this.getAllBacklogLevels();

        const generateRollupColumnOptions = (workItemTypeNames: string[]) => {
            rollupTypes.forEach(rollupType => {
                workItemTypeNames &&
                    workItemTypeNames.forEach(workItemTypeName => {
                        const rollupMetric: RollupCalculationSettings = {
                            type: rollupType,
                            aggregation: RollupAggregation.Count,
                            workItemTypeFilter: workItemTypeName
                        };
                        const resourceString = rollupType === RollupType.Progress ? Resources.ProgressByValue : Resources.TotalByValue;

                        rollupTypeToAvailableRollupColumns[rollupType].push({
                            displayName: format(resourceString, workItemTypeName),
                            referenceName: getRollupColumnReferenceName(rollupMetric),
                            rollupCalculation: rollupMetric
                        });
                    });
            });
        };

        if (backlogConfiguration) {
            if (currentBacklogLevel.value === backlogConfiguration.taskBacklog.name) {
                // If we aren't on portfolio or requirement backlog, don't show any rollup columns
                return [];
            }

            let topLevel: BacklogLevelConfiguration | undefined = allBacklogLevels.find(backlog => currentBacklogLevel.value == backlog.name);
            if (topLevel) {
                const topLevelRank = topLevel.rank;
                allBacklogLevels.forEach(level => {
                    if (level.rank < topLevelRank) {
                        generateRollupColumnOptions(level.workItemTypes.map(type => type.name));
                    }
                });
            } else {
                allBacklogLevels.forEach(backlog => {
                    generateRollupColumnOptions(backlog.workItemTypes.map(type => type.name));
                });
            }

            const effortTypeField = BacklogFieldTypes.Effort;
            const remainingWorkTypeField = BacklogFieldTypes.RemainingWork;

            const typeFields = [effortTypeField, remainingWorkTypeField];
            typeFields.forEach(typeFieldIndex => {
                const field = backlogConfiguration.backlogFields.typeFields[typeFieldIndex];
                const fieldDefinition = this.workItemFieldService.getFieldByReferenceName(field);
                if (fieldDefinition) {
                    const friendlyFieldName = fieldDefinition.name;
                    let applicableRollupTypes: number[] = [];
                    switch (typeFieldIndex) {
                        case remainingWorkTypeField:
                            applicableRollupTypes = [RollupType.Total];
                            break;
                        case effortTypeField:
                            applicableRollupTypes = rollupTypes;
                    }
                    applicableRollupTypes.forEach(rollupType => {
                        const rollupMetric = {
                            type: rollupType,
                            aggregation: RollupAggregation.Sum,
                            aggregationField: field,
                            aggregationFieldDisplayName: friendlyFieldName
                        };

                        let resourceString: string;
                        if (rollupType === RollupType.Progress) {
                            resourceString = Resources.ProgressByValue;
                        } else {
                            resourceString = Resources.TotalByFieldName;
                        }

                        rollupTypeToAvailableRollupColumns[rollupType].push({
                            displayName: format(resourceString, friendlyFieldName),
                            referenceName: getRollupColumnReferenceName(rollupMetric),
                            rollupCalculation: rollupMetric
                        });
                    });
                }
            });
        }
        return rollupTypeToAvailableRollupColumns[RollupType.Progress].concat(rollupTypeToAvailableRollupColumns[RollupType.Total]);
    }

    public getRollupColumnNameAlignedWithBacklog(
        rollupMetric: RollupCalculationSettings,
        friendlyNames?: {
            backlogName?: string;
            fieldName?: string;
        }
    ): string {
        if (!friendlyNames) {
            const allVisibleBacklogLevels = this.getAllVisibleLevels();
            const adjustedBacklogName = allVisibleBacklogLevels.find(backlogLevel => backlogLevel.id === rollupMetric.backlogFilter)?.name;
            if (adjustedBacklogName) {
                friendlyNames = {
                    backlogName: adjustedBacklogName,
                    fieldName: rollupMetric.aggregationFieldDisplayName ?? rollupMetric.aggregationField
                };
            }
        }
        return getRollupColumnDisplayName(rollupMetric, friendlyNames);
    }

    public async getBacklogConfiguration(ignoreCache?: boolean): Promise<IBacklogConfiguration | undefined> {
        if (!this.backlogConfiguration || ignoreCache) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.backlogConfiguration = await contributionService.getDataAsync<IBacklogConfiguration>(
                "ms.vss-work-web.agile-backlog-configuration-data-provider",
                undefined /* requestParameters */,
                ignoreCache
            );
        }
        return this.backlogConfiguration;
    }

    public getBacklogConfigurationSync(): IBacklogConfiguration | undefined {
        if (!this.backlogConfiguration) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.backlogConfiguration = contributionService.getData<IBacklogConfiguration>(
                "ms.vss-work-web.agile-backlog-configuration-data-provider"
            );
        }
        return this.backlogConfiguration;
    }

    public getBacklogLevelId(): string {
        return this.backlogLevelId;
    }

    public getSelectedProviderItems = (item?: ITreeItemEx<IBacklogTreeRow>): ITreeItemEx<IBacklogTreeRow>[] => {
        const treeProviderItems: ITreeItemEx<IBacklogTreeRow>[] = this.backlogTree.value?.itemProvider.value!;

        const selectedProviderItems: ITreeItemEx<IBacklogTreeRow>[] = [
            ...flatten(
                this.getTreeSelection().value.map(range => {
                    const rangeItems: ITreeItemEx<IBacklogTreeRow>[] = [];
                    for (let i = range.beginIndex; i <= range.endIndex; ++i) {
                        rangeItems.push(treeProviderItems[i]);
                    }
                    return rangeItems;
                })
            )
        ];

        if (item && selectedProviderItems.findIndex(selectedItem => selectedItem.underlyingItem.data.id === item.underlyingItem.data.id) === -1) {
            selectedProviderItems.push(item);
        }

        return selectedProviderItems;
    };

    public getSelectedProviderItemsWithChildren = async (item?: ITreeItemEx<IBacklogTreeRow>, checkAllowedForBacklogTypes: boolean = true): Promise<ITreeItemEx<IBacklogTreeRow>[]> => {
        const backlogTree = this.getBacklogTree().value;
        if (!backlogTree) {
            return [];
        }

        const getTreeItemEx = (backlogTreeRow: IBacklogTreeRow) => {
            return backlogTree.itemProvider.value.find(row => row.underlyingItem.data.id === backlogTreeRow.id);
        };

        const selectedTreeItems = this.getSelectedProviderItems(item);
        const uniqueWorkItems = new Map<number, ITreeItemEx<IBacklogTreeRow>>();

        const allowedWorkItemTypesSet = await this.getWorkItemTypesOfBacklogLevels([BacklogColumnSettings.Requirements, BacklogColumnSettings.Tasks]);

        for (const treeItem of selectedTreeItems) {
            // if it's not feature or epic - move all structure 
            const itemType = treeItem.underlyingItem.data.value.workItemTypeColorAndIcon?.workItemTypeName;
            const typeIsNotExcluded = itemType && allowedWorkItemTypesSet.has(itemType);

            if (!checkAllowedForBacklogTypes || (checkAllowedForBacklogTypes && typeIsNotExcluded)) {
                const flattenedTreeRow = await treeItem.underlyingItem.data.getFlattenedArray();
                flattenedTreeRow.forEach(el => {
                    const treeRow = getTreeItemEx(el);
                    if (treeRow) {
                        uniqueWorkItems.set(treeRow.underlyingItem.data.id, treeRow)
                    }
                });
            }
        }

        return Array.from(uniqueWorkItems.values());
    }

    public selectAllItems = (): void => {
        const treeProviderItems = this.backlogTree.value?.itemProvider.value;
        if (treeProviderItems && treeProviderItems.length > 0) {
            this.getTreeSelection().select(0, treeProviderItems.length);
        }
    };

    public getSelectedTreeItems = (item?: ITreeItem<IBacklogTreeRow>): ITreeItem<IBacklogTreeRow>[] => {
        const selectedTreeItems: ITreeItem<IBacklogTreeRow>[] = this.getSelectedProviderItems()?.map(treeItem => treeItem.underlyingItem) ?? [];

        if (item && selectedTreeItems.findIndex(selectedItem => selectedItem.data.id === item.data.id) === -1) {
            selectedTreeItems.push(item);
        }

        return selectedTreeItems;
    };

    public getSelectedItems = (item?: IBacklogTreeRow): IBacklogTreeRow[] => {
        const selectedTreeRowItems: IBacklogTreeRow[] = this.getSelectedProviderItems()?.map(treeItem => treeItem?.underlyingItem?.data) ?? [];

        if (item && !selectedTreeRowItems.some(selectedItem => selectedItem.id === item.id)) {
            selectedTreeRowItems.push(item);
        }
        return selectedTreeRowItems.filter(item => item.id > 0);
    };

    // Used for getting items from the raw backlog tree (not from itemProvider)
    public getSelectedItemsIdFromRawBacklogTree = async (fetchChildren: boolean = false, useFetchChildrenOptimization: boolean = false): Promise<number[]> => {
        const notFetchedWorkItemIdsSet = new Set<number>();
        const selection = this.getTreeSelection();
        const itemProvider = this.getBacklogTree().value?.itemProvider;
        for (const range of selection.value) {
            for (let i = range.beginIndex; i <= range.endIndex; i++) {
                const item = itemProvider?.value[i];
                const iBacklogTreeRow = item?.underlyingItem.data;

                if (!iBacklogTreeRow) {
                    continue;
                }

                if (fetchChildren) {
                    const includeParentNode = true;
                    (await iBacklogTreeRow.getFlattenedArray(undefined, includeParentNode)).forEach(row => {
                        if (useFetchChildrenOptimization && row.value.coreColumnMap[CoreFieldRefNames.Id]) {
                            return;
                        }
                        notFetchedWorkItemIdsSet.add(row.id)
                    });
                } else {
                    if (!iBacklogTreeRow.value.coreColumnMap[CoreFieldRefNames.Id]) {
                        const workItemId = iBacklogTreeRow.id;
                        notFetchedWorkItemIdsSet.add(workItemId);
                    }
                }
            }
        }
        return Array.from(notFetchedWorkItemIdsSet);
    };

    public getAllParents(id: number) {
        return [];
    }

    public getBacklogTree(
        getData?: (options: any) => Promise<ICommonBacklogData | ExceptionInfoResult>,
        options?: {
            updateFiltersOrOrder: boolean;
            ignoreCache: boolean;
        }
    ): IObservableValue<IBacklogTree | undefined> {
        if (getData) {
            this.updateBacklogTree(getData, options);
        }

        return this.backlogTree;
    }

    public getBacklogCommandBarItems = (hubService: IHubContentService) => {
        const currentDirectoryService = this.pageContext.getService<ICurrentDirectoryService>("ICurrentDirectoryService");
        const filterState = this.filter.getState();
        const isNewSprintsViewEnabled = isFeatureFlagEnabled(this.pageContext, FeatureFlags.NewSprintsBacklogsView, false);
        return [
            {
                disabled: !isFilterStateEmpty(mapToFilterState(filterState)),
                iconProps: { iconName: "Add" },
                id: "new-work-item",
                itemType: MenuItemType.Normal,
                onActivate: (menuItem: IMenuItem, e: React.MouseEvent<HTMLElement>) => {
                    this.onClickNewWorkitemBtn(e);
                },
                rank: 1,
                subtle: !isNewSprintsViewEnabled,
                isPrimary: isNewSprintsViewEnabled,
                text: Resources.NewWorkItem
            },

            {
                iconProps: { iconName: "Repair" },
                id: "column-options",
                itemType: MenuItemType.Normal,
                onActivate: () => {
                    this.openDialog(onDismiss => (
                        <WrappedComponent
                            dependencies={["ms.vss-work-web.backlogs-column-options-panel-container"]}
                            wrappedType="backlogs-column-options-container"
                            wrappedProps={{
                                onDismiss: onDismiss,
                                teamId: currentDirectoryService.getCurrentDirectory().value?.id,
                                hubService: hubService
                            }}
                        />
                    ));
                },
                rank: 3,
                subtle: !isNewSprintsViewEnabled,
                text: Resources.ColumnOptions
            },
            {
                iconProps: { iconName: "QueryList" },
                id: "create-query",
                itemType: MenuItemType.Normal,
                onActivate: this.handleCreateQueryAction,
                rank: 4,
                subtle: !isNewSprintsViewEnabled,
                text: Resources.CreateQuery
            },
            WorkItemUtils.getEmailCommandMenuItem(
                this.pageContext,
                this.backlogTree.value!.visibleColumns,
                () => {
                    const filterState = this.filter.getState();
                    const sortedIds = this.backlogContent!.targetIds;
                    if (isFilterStateEmpty(mapToFilterState(filterState) || this.filteredIds.length === 0)) {
                        return sortedIds
                            .map(id => this.allIdsToRowsMap.get(id))
                            .filter(workItem => workItem && workItem.value.coreColumnMap && workItem.value.coreColumnMap[CoreFieldRefNames.Id])
                            .map(workItem => workItem!.value.coreColumnMap);
                    } else {
                        const filteredIdsSet = new Set(this.filteredIds);
                        return sortedIds
                            .filter(id => filteredIdsSet.has(id))
                            .map(id => this.allIdsToRowsMap.get(id))
                            .filter(workItem => workItem && workItem.value.coreColumnMap)
                            .map(workItem => workItem!.value.coreColumnMap);
                    }
                },
                undefined,
                "",
                format(Resources.EmailSubject, this.currentTeam.name, this.allBacklogLevels.find(level => level.id === this.backlogLevelId)?.name)
            )
        ];
    };

    public getAllIdsToRowsMap(): Map<number, IBacklogTreeRow> {
        return this.allIdsToRowsMap;
    }

    public getBacklogWiql(
        getData: (options: any) => Promise<ICommonBacklogData | ExceptionInfoResult>,
        options?: { ignoreCache: boolean }
    ): IReadonlyObservableValue<string> {
        this.ensureBacklogContents(getData, options);
        return this.backlogWiql;
    }

    public getCurrentTeam = (): ITeam => {
        return this.currentTeam;
    };

    public getDataSourceName(): string {
        return CommonBacklogContentService.name;
    }

    public getDisplayState = (): IReadonlyObservableValue<DisplayState> => {
        return this.displayState;
    };

    public getExceptionInfo(): IReadonlyObservableValue<ExceptionInfo | undefined> {
        return this.exceptionInfo;
    }

    public getIsLoadingNextBatchObservable(): IObservableValue<boolean> {
        return this._isLoadingNextBatch;
    }

    public getFilter = (): IFilter => {
        return this.filter;
    };

    public getIds(): number[] {
        return Array.from(this.allIdsToRowsMap.keys());
    }

    public getIsRequirementBacklog = () => {
        return this.isRequirementsBacklog.value;
    };

    public getIsRootBacklog = () => {
        return this.isRootBacklog.value;
    };

    public getItemCount(): number {
        return this.allIdsToRowsMap.size;
    }

    public getNewItemCalloutAnchor = () => {
        return this.newItemCalloutAnchor;
    };

    public getPageContext = (): IVssPageContext => {
        return this.pageContext;
    };

    public getParentWorkItem = (parentId: number) => {
        return this.parentWorkItems.get(parentId);
    };
    public getRecentQueryUrl = () => {
        return this.queryUrl;
    };

    public getShouldShowOrderValues = () => {
        return this.shouldShowOrderValues;
    };

    public getShowFilterHeader = () => {
        return this.showFilterHeader;
    };
    public getShowQueryHeader = () => {
        return this.showQueryHeader;
    };

    public getTeamAreaNodes = () => {
        return this.teamAreaNodesById;
    };

    public getTeamField = () => {
        return this.teamSettings.value?.teamFieldName;
    };

    public getTreeSelection = (): ListSelection => {
        return this.treeSelection;
    };

    public getBacklogWorkItemTypeNames = (): string[] => {
        return this.backlogWorkItemTypeNames;
    };

    public getBacklogDefaultWorkItemTypeName = async (teamId?: string, bagklogLevelId?: string): Promise<string | undefined> => {
        const allBacklogLevels = await this.getAllBacklogLevels(teamId);
        const currentBacklogLevelId = bagklogLevelId ?? this.getBacklogLevelId();
        const currentBacklogLevel = allBacklogLevels.find(
            backlogLevel => backlogLevel.id === currentBacklogLevelId || backlogLevel.name === currentBacklogLevelId
        );
        return currentBacklogLevel?.defaultWorkItemType.name;
    };

    public getRollupData(id: number, rollupCalculation: RollupCalculationSettings): IObservableValue<RollupProgressBarData | undefined> {
        const rollupProviders = this.getRollupColumnProviders();
        const provider = rollupProviders?.find(pro => isRollupEqual(pro.getRollupMetric(), rollupCalculation));
        if (provider) {
            provider.checkAndUpdateWorkItemIds([id]);
            return provider.getRollupData(id, true);
        }
        return new ObservableValue(undefined);
    }

    public async getUniqueValues(fieldName: string): Promise<FilterValue[]> {
        const uniqueValues = new Set<string>();

        // special case when field value isn't a string
        const uniqueObjects: IdentityRef[] = [];
        if (!this.backlogTree.value) {
            return [];
        }

        const rows = Object.values(this.backlogTree.value.allRows);

        if (this.backlogDelayLoadIdentitiesIsEnabled) {
            if (fieldName === CoreFieldRefNames.AssignedTo) {
                var assignedToRows = [...new Set(Object.values(rows).filter(row => row.value.coreColumnMap[CoreFieldRefNames.AssignedTo] && !row.value.isIdentityResolved))];
                await this._beginResolveIdentityFields(assignedToRows);
            }
        }

        for (const item of rows) {
            const fieldValue = item.value.coreColumnMap[fieldName];

            if (!fieldValue) {
                continue;
            }

            if (fieldName === CoreFieldRefNames.Tags) {
                const tags = (fieldValue as string).split(";");
                tags.forEach(t => uniqueValues.add(t.trim()));
            } else if (fieldName === CoreFieldRefNames.AssignedTo) {
                getUniqueValuesForAssignedTo(this.pageContext, fieldValue, uniqueValues, uniqueObjects);
            } else if (fieldName === CoreFieldRefNames.AreaPath) {
                uniqueValues.add(getAreaPathLeafNode(fieldValue.toString()));
            } else {
                uniqueValues.add(fieldValue.toString());
            }
        }

        if (fieldName === CoreFieldRefNames.WorkItemType && this.allBacklogLevels) {
            const currentBacklogLevelRank = this.allBacklogLevels.find(item => item.id === this.backlogLevelId)?.rank;

            if (currentBacklogLevelRank) {
                this.allBacklogLevels
                    .filter(level => level.rank < currentBacklogLevelRank)
                    .forEach(level => {
                        level.workItemTypes.forEach(type => {
                            uniqueValues.add(type.name);
                        });
                    });
            }
        }

        return uniqueObjects.length > 0
            ? uniqueObjects.sort((a, b) => localeIgnoreCaseComparer(a.displayName, b.displayName))
            : Array.from(uniqueValues).sort(localeIgnoreCaseComparer);
    }

    public getParentItems(): IParentFilterItem[] {
        return Array.from(this.parentWorkItems.values())
            .filter(item => item.value && !this.allIdsToRowsMap.has(item.value.id))
            .map(item => {
                return { id: item.value!.id, title: item.value!.getFieldValueByName(CoreFieldRefNames.Title) };
            })
            .sort();
    }

    public getParentWorkItems(): Map<number, ObservableValue<IWorkItem | undefined>> {
        return this.parentWorkItems;
    }

    /**
     * Gets the data for a single work item.
     * @param id workItemId of the work item to retrieve data for.
     */
    public getValue(id: number, fieldName: string): any {
        if (!this.backlogTree.value || !this.backlogTree.value.allRows) {
            return;
        }
        const workItemData = this.allIdsToRowsMap.get(id);
        return workItemData?.value.coreColumnMap[fieldName];
    }

    public getVisibleFields(): string[] {
        return this.backlogContent?.columns.map(col => col.name) ?? [];
    }

    public getQueryType(): BacklogQueryResultType | undefined {
        return this.backlogContent?.backlogQueryResultType;
    }

    private getWorkItemTypesOfBacklogLevel(backlogLevelId: string | undefined): string[] | undefined {
        if (!backlogLevelId) {
            return;
        }

        const workItemTypes = this.allBacklogLevels?.find(level => level.id === backlogLevelId)?.workItemTypes;
        return workItemTypes?.map(type => type.name);
    }

    public getWorkItemIdsSet(): Set<number> {
        if (!this.workItemIdsSet) {
            this.workItemIdsSet = new Set<number>();
            if (this.backlogTree.value) {
                Object.values(this.backlogTree.value?.allRows)
                    .filter(item => !item.value.coreColumnMap[CoreFieldRefNames.State])
                    .forEach(item => {
                        this.workItemIdsSet!.add(item.id);
                    });
            }
        }
        return this.workItemIdsSet;
    }

    public processFetchedWorkItems = async (
        response: IPageWorkItemsDataProviderResponse,
        workItemStateAndColorDict: { [workItemType: string]: WorkItemStateAndColor[] },
        workItemTypeColorAndIcons: WorkItemTypeColorAndIcon[]
    ) => {
        const rollupProviders = this.getRollupColumnProviders() ?? [];
        const childItems: { [workItemId: number]: WorkItemTypeColorAndIcon[] } = await this.getChildWorkItemTypes(
            this.backlogTree.value?.columnMap,
            response.data.rows,
            workItemTypeColorAndIcons
        );
        // convert the raw row data into something resembling a backlog item that fits in the tree
        const newBacklogItems: [
            id: number,
            item: {
                columnValues: RawRowData[];
                coreColumnMap: { [columnName: string]: RawRowData };
                workItemStateAndColor: WorkItemStateAndColor | undefined;
                workItemTypeColorAndIcon: WorkItemTypeColorAndIcon | undefined;
            }
        ][] = [];

        for (const row of response.data.rows) {
            const { id, backlogItemUpdates } = await this.parseBacklogItemRowData(
                row,
                response.data.columns,
                workItemStateAndColorDict,
                workItemTypeColorAndIcons
            );
            if (rollupProviders) {
                for (const provider of rollupProviders) {
                    if (!provider.hasWorkItem(id)) {
                        provider.checkAndUpdateWorkItemIds([id]);
                    }
                }
            }

            newBacklogItems.push([id, backlogItemUpdates]);
        }

        await this.setParents([...this.parentWorkItems].filter(([key, item]) => !item.value).map(([key, item]) => key));

        newBacklogItems.forEach(([id, item]) => {
            this.workItemIdsSet?.delete(id);
            const backlogTree = this.backlogTree.value!;
            //replace the entire IBacklogItem value so that observers are notified of the change
            const backlogItem = backlogTree.allRows[id].value;
            backlogTree.allRows[id].value = {
                childWorkItemTypes: childItems[id],
                columnValues: this.updateRollupDataForNewItem(id, item.columnValues),
                coreColumnMap: item.coreColumnMap,
                isValid: backlogItem.isValid,
                order: backlogItem.order,
                workItemStateAndColor: item.workItemStateAndColor,
                workItemTypeColorAndIcon: item.workItemTypeColorAndIcon,
                workItemUrl: backlogItem.workItemUrl
            };
            this.allIdsToRowsMap.set(id, backlogTree.allRows[id]);
        });
        if (!this.newBacklogRerenderingOnFilterBehavior) {
            this.resetOrderColumn();
        }
    };

    public refreshTree = async (options?: {
        getData?: (options: any) => Promise<ICommonBacklogData | ExceptionInfoResult>;
        keepOpen?: boolean;
        filterManager?: FilterManager;
    }): Promise<void> => {
        if (!options?.keepOpen) {
            this.clearCache();
        }
        if (options?.getData) {
            await this.updateBacklogTree(options?.getData, {
                updateFiltersOrOrder: true,
                ignoreCache: true,
                keepOpen: options?.keepOpen,
                filterManager: options?.filterManager
            });
        }
    };

    public resetOrderColumn(suspendNotifications?: boolean) {
        let order = 1;
        if (this.shouldShowOrderValues) {
            const itemProvider = this.backlogTree.value?.itemProvider;
            this.backlogTree.value?.rows.forEach(item => {
                const providerItem = itemProvider?.value.find(treeProviderItem => treeProviderItem.underlyingItem.data.id === item.data.id);
                const backlogItem = item.data.value;
                if (!item.data.parent) {
                    // rerender only parent items
                    backlogItem.order = this.getWorkItemOrder(item.data.id > 0, order);
                    if (backlogItem.order) {
                        order++;
                    }
                    if (providerItem) {
                        providerItem.underlyingItem.data.value = backlogItem;
                    }
                }
            });
            if (!suspendNotifications) {
                this.backlogTree.notify(this.backlogTree.value, "change");
            }
        }
    }

    public findClosestItemWithinItemProvider(
        itemProvider: TreeItemProvider<IBacklogTreeRow>,
        insertAfterTreeProviderItem: ITreeItemEx<IBacklogTreeRow>
    ): ITreeItem<IBacklogTreeRow> | undefined {
        if (!insertAfterTreeProviderItem.underlyingItem.data.value.order) {
            return insertAfterTreeProviderItem.underlyingItem;
        }
        if (itemProvider.value.some(item => item.underlyingItem.data.id === insertAfterTreeProviderItem?.underlyingItem.data.id)) {
            return insertAfterTreeProviderItem.underlyingItem;
        }

        let insertAfter = undefined;
        const limitOrder = insertAfterTreeProviderItem.underlyingItem.data.value.order;
        // select all items with specified order (and ensure that order is defined)
        const lowerOrderItems = itemProvider.value.filter(treeProviderItem => {
            const currentOrder = treeProviderItem.underlyingItem.data.value.order;
            if (currentOrder && currentOrder < limitOrder) {
                return treeProviderItem;
            }
        });
        if (lowerOrderItems.length > 0) {
            let maxItem = lowerOrderItems[0].underlyingItem;
            for (const providerItem of lowerOrderItems) {
                const item = providerItem.underlyingItem;
                if (item.data.value.order! > maxItem.data.value.order!) {
                    maxItem = item;
                }
            }
            insertAfter = maxItem;
        }
        return insertAfter;
    }

    public insertItems(
        selectedProviderItems: ITreeItemEx<IBacklogTreeRow>[],
        insertAfterTreeProviderItem?: ITreeItemEx<IBacklogTreeRow>,
        insertIndex?: number
    ): void {
        const selectedItems: ITreeItem<IBacklogTreeRow>[] = selectedProviderItems.map(providerItem => providerItem.underlyingItem);
        const itemProvider: TreeItemProvider<IBacklogTreeRow> | undefined = this.getBacklogTree().value?.itemProvider;

        if (!insertAfterTreeProviderItem && insertIndex !== undefined) {
            insertAfterTreeProviderItem = insertIndex >= 0 ? itemProvider?.value[insertIndex] : undefined;
        }

        if (!itemProvider || !selectedItems.length) {
            return;
        }

        let reorderedItemsParent: ITreeItemEx<IBacklogTreeRow> | undefined = undefined;
        const reorderedItemsDepth = selectedProviderItems[0].depth;
        // Map over items, find their parent, then remove them.  Can grab items from different parents to reorder.
        selectedProviderItems.forEach(selectedProviderItem => {
            reorderedItemsParent = selectedProviderItem.parentItem;
            itemProvider.splice(reorderedItemsParent && reorderedItemsParent.underlyingItem, selectedItems);
        });

        // If item is reordered to the first child of a parent then insertAfter must be undefined
        if (reorderedItemsParent === insertAfterTreeProviderItem || reorderedItemsDepth - 1 === insertAfterTreeProviderItem?.depth) {
            // If parent is not expanded, expand it so it can be seen on the treeProvider
            if (insertAfterTreeProviderItem?.underlyingItem && !insertAfterTreeProviderItem?.underlyingItem?.expanded) {
                itemProvider.toggle(insertAfterTreeProviderItem.underlyingItem);
            }

            itemProvider.splice(insertAfterTreeProviderItem?.underlyingItem, undefined, [
                {
                    insertAfter: undefined,
                    items: selectedItems
                }
            ]);
        } else {
            let parentItem = insertAfterTreeProviderItem?.parentItem?.underlyingItem;
            let insertAfterItem = this.findClosestItemWithinItemProvider(itemProvider, insertAfterTreeProviderItem);

            // If inserting after item of different depth, insert after same level item
            if (insertAfterTreeProviderItem?.depth !== reorderedItemsDepth) {
                parentItem = undefined;
                insertAfterItem = insertAfterTreeProviderItem?.parentItem?.underlyingItem;
            }

            itemProvider.splice(parentItem, undefined, [
                {
                    insertAfter: insertAfterItem,
                    items: selectedItems
                }
            ]);
        }

        // Move rows to another index within backlogTree.rows collection
        this.reorderBacklogTreeRows(selectedProviderItems, insertAfterTreeProviderItem?.underlyingItem);

        // After drop, handle re-numbering from 1 to n for "Order" column for level 0 work items only
        this.resetOrderColumn();
    }

    public setHubType(hub: HubType) {
        this.hub = hub;
    }

    public setShouldShowOrderValues(showOrder: boolean) {
        this.shouldShowOrderValues = showOrder;
    }

    public settingsServiceGetColumns = async (settingKey: string, levelKey?: string): Promise<IBacklogColumn[]> => {
        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        const backlogLevelKey = levelKey ?? this.getBacklogLevelKey();

        const result: { [key: string]: any } = await settingsService.getEntriesAsync(
            settingKey,
            SettingsUserScope.Me,
            BacklogColumnSettings.TeamScope,
            this.getCurrentTeam().id
        );
        const userVisibleColumns = result[backlogLevelKey];
        let columns: IBacklogColumn[] = [];

        // If user's first time modifying columns, settingService will be blank. Can get columns from backlogTree
        if (!userVisibleColumns) {
            const visibleColumns = this.backlogTree.value?.visibleColumns;
            return visibleColumns!;
        }

        if (userVisibleColumns) {
            columns = JSON.parse(userVisibleColumns);
        }

        return columns;
    };

    public settingsServiceSetColumns = async (settingKey: string, backlogColumns: IBacklogColumn[], levelKey?: string): Promise<void> => {
        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        const backlogLevelKey = levelKey ?? this.getBacklogLevelKey();

        const key = format(settingKey, backlogLevelKey);
        const JSONColumns = JSON.stringify(backlogColumns);

        await settingsService.setEntries({ [key]: JSONColumns }, SettingsUserScope.Me, BacklogColumnSettings.TeamScope, this.getCurrentTeam().id);
    };

    public settingsServiceResizeColumn = async (settingKey: string, resizedColumnId: number, newWidth: number, levelKey?: string): Promise<void> => {
        const columns = await this.settingsServiceGetColumns(settingKey);

        let columnIndex = columns.findIndex(column => resizedColumnId === column.id);
        if (columnIndex === -1) {
            columnIndex = columns.findIndex(column => resizedColumnId === column.fieldId);
        }

        if (columnIndex !== -1) {
            columns[columnIndex].width = newWidth;
            this.settingsServiceSetColumns(`${settingKey}/{0}`, columns, levelKey);
        }
    };

    public showBulkSaveErrorMessages = (errors: string[]) => {
        const uniqueErrors = [...new Set(errors.filter(error => error !== null && error !== undefined))];
        this.messagesService.showMessage(getErrorMessage(Resources.BulkSaveErrorMessage, uniqueErrors));
    };

    public showBulkSaveErrorMessage = (error: IWorkItemSavingFailedError, severity?: MessageCardSeverity) => {
        const moreDetails: any[] = [];

        if (error.results) {
            error.results.forEach(result => {
                if (result.error) {
                    moreDetails.push(result.error.message);
                }
            });
        }
        this.messagesService.showMessage(getErrorMessage(Resources.BulkSaveErrorMessage, [...new Set(moreDetails)], undefined, severity));
    };

    public setDisplayState(displayState: DisplayState) {
        this.displayState.value = displayState;
    }

    public showErrorMessage = (error: any, severity?: MessageCardSeverity) => {
        this.messagesService.showMessage(getErrorMessage(error, undefined, undefined, severity));
    };

    public setExceptionInfo = (exceptionInfo: ExceptionInfo | undefined) => {
        this.exceptionInfo.value = exceptionInfo;
    };

    public setNumberFilteredItems = (itemsPaged: number) => {
        this.filteredItems = itemsPaged;
    };

    public teamOwnsWorkItem = (areaPath: string): boolean => {
        if (!areaPath) {
            return false;
        }

        const areaMap = this.getTeamAreaNodes();
        if (areaMap.has(areaPath)) {
            return true;
        } else {
            for (const [field, fieldData] of areaMap.entries()) {
                if (fieldData.includeChildren && areaPath.startsWith(field)) {
                    return true;
                }
            }
            return false;
        }
    };

    public reorderBacklogTreeRows(
        selectedProviderItems: ITreeItemEx<IBacklogTreeRow>[],
        insertAfterItem: ITreeItem<IBacklogTreeRow> | undefined
    ): void {
        let rows = this.backlogTree.value?.rows;
        if (!rows) {
            return;
        }

        const savedRows: ITreeItem<IBacklogTreeRow>[] = [];
        const itemIdToFound: { [id: number]: boolean } = {};
        const totalNumber = selectedProviderItems.length;

        selectedProviderItems.forEach(item => {
            itemIdToFound[item.underlyingItem.data.id] = false;
        });

        const deleteItems = (list: ITreeItem<IBacklogTreeRow>[]) => {
            return list.filter(item => {
                if (savedRows.length === totalNumber) {
                    return true;
                }

                const id = item.data.id;
                if (itemIdToFound[id] !== undefined && !itemIdToFound[id]) {
                    savedRows.push(item);
                    itemIdToFound[id] = true;
                    return false;
                }

                if (item.childItems) {
                    item.childItems = deleteItems(item.childItems);
                    if (item.childItems.length === 0) {
                        item.childItems = undefined;
                    }
                }

                return true;
            });
        };

        rows = deleteItems(rows);

        const addItems = (list: ITreeItem<IBacklogTreeRow>[], insertAfter: ITreeItem<IBacklogTreeRow>) => {
            const stack: { rows: ITreeItem<IBacklogTreeRow>[]; depth: number }[] = [{ rows: list, depth: 0 }];
            let isItemsInserted = false;

            while (stack.length > 0 && !isItemsInserted) {
                const { rows, depth } = stack.pop()!;

                for (let i = 0; i < rows.length; i++) {
                    const row = rows[i];
                    if (row.data.id === insertAfter.data.id) {
                        if (depth === selectedProviderItems[0].depth) {
                            rows.splice(i + 1, 0, ...savedRows);
                        } else {
                            row.childItems ? row.childItems.splice(0, 0, ...savedRows) : (row.childItems = [...savedRows]);
                        }

                        isItemsInserted = true;
                        break;
                    }

                    if (row.childItems) {
                        stack.push({ rows: row.childItems, depth: depth + 1 });
                    }
                }
            }
        };

        if (savedRows.length > 0) {
            if (insertAfterItem === undefined) {
                rows.splice(0, 0, ...savedRows);
            } else {
                addItems(rows, insertAfterItem);
            }

            this.backlogTree.value!.rows = rows;
        }
    }

    public async updateWorkItemRow(workItemId: number, workItem?: IWorkItem, forceRefresh?: boolean): Promise<void> {
        const updateRowData = (backlogItem: IBacklogItem) => {
            const fieldValues = backlogItem.coreColumnMap;
            for (const fieldRefName of Object.keys(fieldValues)) {
                const currentValue = workItem?.getFieldValueByName(fieldRefName);
                const oldValue = fieldValues[fieldRefName];
                if (currentValue !== oldValue) {
                    fieldValues[fieldRefName] = currentValue;
                }
            }
        };
        const rollupProviders = this.getRollupColumnProviders();
        rollupProviders?.forEach(provider => provider.resetWorkItemList(this.backlogTree.value!.rows.map(row => row.data.id)));
        if (forceRefresh || !workItem) {
            const workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
            workItem = await workItemModelService.getWorkItem(workItemId);
            await workItem.refresh();
        }
        return this.updateTreeLocally([workItem], updateRowData);
    }
    // The implementation for batch update of multiple rows, so that final List component with Tree rows will be rerendered
    // constant amount of times. The logic is the same as with updateWorkItemRow function in this class except if processes
    // not a single row, but array of rows.
    public async updateWorkItemRows(rows: { workItemId: number; workItem?: IWorkItem; forceRefresh?: boolean }[]): Promise<void> {
        const updateRowData = (backlogItem: IBacklogItem) => {
            for (const row of rows) {
                if (backlogItem.coreColumnMap[CoreFieldRefNames.Id] === row.workItem?.getFieldValueByName(CoreFieldRefNames.Id)) {
                    const fieldValues = backlogItem.coreColumnMap;
                    for (const fieldRefName of Object.keys(fieldValues)) {
                        const currentValue = row.workItem?.getFieldValueByName(fieldRefName);
                        const oldValue = fieldValues[fieldRefName];
                        if (currentValue !== oldValue) {
                            fieldValues[fieldRefName] = currentValue;
                        }
                    }
                }
            }
        };

        const workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        const rollupProviders = this.getRollupColumnProviders();
        rollupProviders?.forEach(provider => provider.resetWorkItemList(this.backlogTree.value!.rows.map(row => row.data.id)));
        for (const row of rows) {
            if (row.forceRefresh || !row.workItem) {
                row.workItem = await workItemModelService.getWorkItem(row.workItemId);
                await row.workItem.refresh();
            }
        }

        return this.updateTreeLocally(
            rows.map(row => row.workItem!),
            updateRowData
        );
    }

    public async updateTreeLocally(
        workItems: IWorkItem[],
        updateColumns: (itemValue: IBacklogItem) => void,
        needToUpdateChildNodes?: boolean,
        removeCondition?: (workItem: IWorkItem, itemValue: IBacklogItem) => boolean,
        updateBacklogItemCoreColumnDataForChildren?: boolean
    ): Promise<void> {
        const workItemConfigurationService = this.pageContext.getService<IBacklogWorkItemConfigurationService>(
            "IBacklogWorkItemConfigurationService"
        );
        const workItemStateAndColorPromise = await workItemConfigurationService.getWorkItemStateAndColorDictionary();
        const workItemTypeColorAndIconsPromise = await workItemConfigurationService.getWorkItemTypeColorAndIcons();
        const [workItemStateAndColor, workItemTypeColorAndIcons] = await Promise.all([
            workItemStateAndColorPromise,
            workItemTypeColorAndIconsPromise
        ]);
        const batchToUpdate: {
            parentItem: ITreeItem<IBacklogTreeRow> | undefined;
            itemsToRemove?: ITreeItem<IBacklogTreeRow>[];
            itemsToAdd?: ITreeItemAdd<IBacklogTreeRow>[];
        }[] = [];
        const itemProvider = this.backlogTree.value?.itemProvider;
        workItems.map(workItem => {
            const itemIndex = itemProvider?.value.findIndex(x => x.underlyingItem.data.id === workItem.id);

            if (itemProvider && itemIndex === -1 && needToUpdateChildNodes) {
                this.updateTreeChildNodes(workItem.id, updateColumns, updateBacklogItemCoreColumnDataForChildren);
            }

            if (itemProvider && itemIndex === -1 && removeCondition) {
                this.removeChildNodesFromTree(workItem, removeCondition);
            }

            if (itemProvider && itemIndex !== undefined && itemIndex !== -1) {
                const item = itemProvider.value[itemIndex];
                const itemValue = item?.underlyingItem?.data?.value;
                const parent = item?.parentItem?.underlyingItem;

                if (itemValue) {
                    const originalType = itemValue.coreColumnMap[CoreFieldRefNames.WorkItemType];
                    const originalState = itemValue.coreColumnMap[CoreFieldRefNames.State];
                    const originalParent = itemValue.coreColumnMap[CoreFieldRefNames.Parent];
                    updateColumns(itemValue);
                    this.updateBacklogItemCoreColumnData(itemValue, workItem.id);

                    // if state or type changed, update state metadata
                    const currentType = itemValue.coreColumnMap[CoreFieldRefNames.WorkItemType] as string;
                    const currentState = itemValue.coreColumnMap[CoreFieldRefNames.State] as string;
                    const currentParent = itemValue.coreColumnMap[CoreFieldRefNames.Parent] as number | null;
                    if (currentType && currentState
                        && (originalState !== currentState
                            || originalType !== currentType
                            || itemValue.workItemStateAndColor?.name !== currentState)) {
                        itemValue.workItemStateAndColor = workItemStateAndColor[currentType]?.find(
                            stateAndColor => stateAndColor.name === currentState
                        );
                    }

                    // if type changed, update type metadata
                    if (currentType && currentType !== originalType) {
                        itemValue.workItemTypeColorAndIcon = workItemTypeColorAndIcons.find(
                            typeAndColor => typeAndColor.workItemTypeName === currentType
                        );
                    }

                    // if parent has changed && parent doesn't exist in our map of parent items right now, go fetch it
                    if (currentParent && currentParent !== originalParent && !this.parentWorkItems.get(currentParent)) {
                        this.parentWorkItems.set(currentParent, new ObservableValue(undefined));
                    }
                }

                batchToUpdate.push({ parentItem: parent, itemsToRemove: [item.underlyingItem] });

                item.underlyingItem.data.value = itemValue;

                let prevItemIndex = itemIndex - 1;
                let insertAfterTreeItem = itemProvider.value[prevItemIndex];

                if (item.parentItem === insertAfterTreeItem || item.depth - 1 === insertAfterTreeItem.depth) {
                    batchToUpdate.push({ parentItem: insertAfterTreeItem?.underlyingItem, itemsToAdd: [{ items: [item.underlyingItem] }] });
                } else {
                    // If insertAfter is a different depth, find the next same depth item to pass in for insertAfter
                    while (insertAfterTreeItem.depth !== item.depth) {
                        prevItemIndex--;
                        insertAfterTreeItem = itemProvider.value[prevItemIndex];
                    }

                    batchToUpdate.push({
                        parentItem: parent,
                        itemsToAdd: [{ items: [item.underlyingItem], insertAfter: insertAfterTreeItem?.underlyingItem }]
                    });
                }
            }
        });

        itemProvider?.spliceBatch(batchToUpdate);

        // Fetch new parents
        this.setParents([...this.parentWorkItems].filter(([key, item]) => !item.value).map(([key, item]) => key));
    }

    private updateBacklogItemCoreColumnData(itemValue: IBacklogItem, workitemId: number) {
        const columnMap = this.backlogTree.value?.columnMap;
        if (columnMap) {
            const columnValues = [];

            for (const fieldRefName of Object.keys(columnMap)) {
                const value = itemValue.coreColumnMap[fieldRefName];
                const index = columnMap[fieldRefName];
                if (index !== undefined) {
                    columnValues[index] = value;
                }
            }

            // add rollup columns to new column value calculation
            itemValue.columnValues = this.updateRollupDataForNewItem(Number(workitemId), columnValues);
        }
    }

    private updateTreeChildNodes(
        workItemId: number,
        updateColumns: (itemValue: IBacklogItem) => void,
        updateBacklogItemCoreColumnDataForChildren?: boolean
    ): void {
        const itemProvider = this.backlogTree.value?.itemProvider!;
        const roots = itemProvider.roots;
        for (const rootItem of roots) {
            this.updateTreeChildNodesInternal(workItemId, updateColumns, itemProvider, rootItem, updateBacklogItemCoreColumnDataForChildren);
        }
    }

    private updateTreeChildNodesInternal(
        workItemId: number,
        updateColumns: (itemValue: IBacklogItem) => void,
        treeItemProvider: TreeItemProvider<IBacklogTreeRow>,
        parent: ITreeItem<IBacklogTreeRow>,
        updateBacklogItemCoreColumnDataForChildren?: boolean
    ): void {

        if (!parent.childItems && updateBacklogItemCoreColumnDataForChildren) {
            //during filtering, we don't have childItems for parent, but we need to update backlogItemCoreColumnData for children
            for (const childItem of parent.data.children) {
                if (childItem.id === workItemId) {
                    updateColumns(childItem.value);
                    this.updateBacklogItemCoreColumnData(childItem.value, workItemId);
                    return;
                }

            }
            return;
        }

        if (!parent.childItems) {
            return;
        }

        for (const childItem of parent.childItems) {
            if (childItem?.childItems) {
                this.updateTreeChildNodesInternal(workItemId, updateColumns, treeItemProvider, childItem, updateBacklogItemCoreColumnDataForChildren);
            }

            if (childItem.data.id === workItemId) {
                updateColumns(childItem.data.value);
                if (updateBacklogItemCoreColumnDataForChildren) {
                    this.updateBacklogItemCoreColumnData(childItem.data.value, workItemId);
                }
            }
        }
    }

    private removeChildNodesFromTree(workItem: IWorkItem, removeCondition: (workItem: IWorkItem, itemValue: IBacklogItem) => boolean): void {
        const itemProvider = this.backlogTree.value?.itemProvider;
        if (itemProvider) {
            const roots = itemProvider.roots;
            for (const rootItem of roots) {
                this.removeChildNodesFromTreeInternal(workItem, removeCondition, itemProvider, rootItem);
            }
        }
    }

    private removeChildNodesFromTreeInternal(
        workItem: IWorkItem,
        removeCondition: (workItem: IWorkItem, itemValue: IBacklogItem) => boolean,
        treeItemProvider: TreeItemProvider<IBacklogTreeRow>,
        parent: ITreeItem<IBacklogTreeRow>
    ): void {
        if (!parent.childItems) {
            return;
        }

        let i = parent.childItems.length;
        while (i--) {
            const childItem = parent.childItems[i];

            if (childItem.childItems) {
                this.removeChildNodesFromTreeInternal(workItem, removeCondition, treeItemProvider, childItem);
            }

            const needToRemove = removeCondition(workItem, childItem.data.value);
            if (needToRemove) {
                treeItemProvider.remove(childItem);
                parent.childItems?.splice(i, 1);
            }
        }
    }

    private async buildBacklogTree(
        getData: (options: any) => Promise<ICommonBacklogData | ExceptionInfoResult>,
        options?: { ignoreCache: boolean; keepOpen?: boolean; filterManager?: FilterManager }
    ): Promise<IBacklogTree | undefined> {

        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.addSplitTiming("Boards.buildBacklogTree.start");

        try {

            const expandedItemIds = options?.keepOpen ? this.getExpandedItemIds() : new Set();

            const workItemConfigurationService = this.pageContext.getService<IBacklogWorkItemConfigurationService>(
                "IBacklogWorkItemConfigurationService"
            );

            const workItemStateAndColorDictPromise = workItemConfigurationService.getWorkItemStateAndColorDictionary();
            const workItemTypeColorAndIconsPromise = workItemConfigurationService.getWorkItemTypeColorAndIcons();

            await this.ensureBacklogContents(getData, options);

            if (this.backlogContent) {
                const content = this.backlogContent;

                const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");
                const tfsPageData = this.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
                const projectName = tfsPageData?.project?.name ?? "";

                // create a map of column types to their index (ie: index 7 is column "System.Id")
                let columnMap = content.payload.columns.reduce((map: { [columnId: string]: number }, columnId, index) => {
                    map[columnId] = index;
                    return map;
                }, {});

                // first, map the row data to its id
                const systemIdIndex = content.payload.columns.indexOf(CoreFieldRefNames.Id);
                const idToColumns = content.payload.rows.reduce((dict: { [id: number]: RawRowData[] }, row: RawRowData[]) => {
                    dict[row[systemIdIndex] as number] = row;
                    return dict;
                }, {});

                // keep track of a bunch of stuff that the UI may use for filtering
                const workItemStateAndColorDict = await workItemStateAndColorDictPromise;
                const workItemTypeColorAndIcons = await workItemTypeColorAndIconsPromise;

                const childItems: { [workItemId: number]: WorkItemTypeColorAndIcon[] } = await this.getChildWorkItemTypes(
                    columnMap,
                    content.payload.rows,
                    workItemTypeColorAndIcons
                );
                const rollupProviders = this.getRollupColumnProviders(true) ?? [];

                // now, taking the combination of targetId (workItem id) and sourceId (workItem parent id)
                // use the previous idToRows dictionary to create a new tree
                const collections: {
                    ids: {
                        targetId: number;
                        sourceId: number;
                    }[];
                    allRows: { [id: number]: IBacklogTreeRow };
                    visibleRows: IBacklogTreeRow[];
                } = { ids: content.targetIds.map((t, i) => ({ targetId: t, sourceId: content.sourceIds[i] })), allRows: {}, visibleRows: [] };

                let orderColumn = 1;

                for (const item of collections.ids) {
                    const { targetId, sourceId } = item;

                    // it's safe to assign parent here, because the data provider guarantees (?) that sourceId has already been seen as a targetId
                    const columns = idToColumns[targetId] ?? [];

                    const { id, backlogItemUpdates } = await this.parseBacklogItemRowData(
                        columns,
                        content.payload.columns,
                        workItemStateAndColorDict,
                        workItemTypeColorAndIcons
                    );

                    const rowOrder = this.getWorkItemOrder(sourceId === 0 && id > 0, orderColumn);
                    if (rowOrder) {
                        orderColumn++;
                    }

                    const row = new ObservableBacklogItem(targetId, sourceId > 0 ? collections.allRows[sourceId] : undefined, {
                        childWorkItemTypes: childItems[targetId],
                        columnValues: columns,
                        coreColumnMap: backlogItemUpdates.coreColumnMap,
                        isValid: true,
                        order: rowOrder,
                        workItemUrl: locationService.routeUrl("ms.vss-work-web.work-items-form-route", {
                            id: targetId.toString(),
                            project: projectName
                        }),
                        workItemStateAndColor: backlogItemUpdates.workItemStateAndColor,
                        workItemTypeColorAndIcon: backlogItemUpdates.workItemTypeColorAndIcon
                    });

                    // ensure this item is included in rollup providers
                    if (rollupProviders) {
                        for (const provider of rollupProviders) {
                            if (!provider.hasWorkItem(row.id)) {
                                provider.checkAndUpdateWorkItemIds([row.id]);
                            }
                        }
                    }

                    // if there's a parent, add this new row to its children
                    // otherwise, it's a root item and goes in the visibleRows array
                    if (row.parent) {
                        row.parent.children.push(row);
                    } else {
                        collections.visibleRows.push(row);
                    }

                    // and all rows get added to this collection, for lookup
                    collections.allRows[targetId] = row;
                }

                Object.entries(collections.allRows).forEach(([id, row]) => this.allIdsToRowsMap.set(Number(id), row));
                await this.getCorrectIdentityData(collections.allRows);

                const levelWorkItemTypes = this.allBacklogLevels.find(
                    level => level.id === this.backlogLevelId || level.name === this.backlogLevelId
                )?.workItemTypes;

                const mapRowToTreeItem = (row: IBacklogTreeRow, isExpanded: boolean): ITreeItem<IBacklogTreeRow> => {
                    // expand all parents above our current backlog level
                    if (
                        isExpanded &&
                        levelWorkItemTypes &&
                        (row.value.columnValues.length === 0 ||
                            levelWorkItemTypes.some(type => type.name === row.value.coreColumnMap[CoreFieldRefNames.WorkItemType]) ||
                            expandedItemIds.size > 0)
                    ) {
                        // Once we find our current level, don't expand anything further
                        isExpanded = false;
                    }
                    const childItems = row.children.length > 0 ? row.children.map(child => mapRowToTreeItem(child, isExpanded)) : undefined;
                    return {
                        expanded: expandedItemIds.has(row.id) || isExpanded,
                        childItems: childItems,
                        data: row
                    };
                };

                await this.getAllBacklogLevels();
                let rows: ITreeItem<IBacklogTreeRow>[] = this.setUnparentedFolderItems(collections.visibleRows, mapRowToTreeItem);
                rows = await this.getRowRollupData(rollupProviders, rows);

                const itemProvider = new TreeItemProvider(rows);
                this.setParents([...this.parentWorkItems.keys()]).then(_ => {
                    if (
                        this.parentWorkItems.size &&
                        options?.filterManager?.isFiltering() &&
                        content.columns.some(c => c.name === CoreFieldRefNames.Parent)
                    ) {
                        options.filterManager.dataUpdated();
                        this.filter.setState(this.filter.getState());
                    }
                });
                await Promise.all(
                    this.columnPreferences.map(async col => {
                        if (col.rollup) {
                            col.rollupCalculation.aggregationFieldDisplayName = await this.getFieldDefinitionDisplayName(
                                col.rollupCalculation.aggregationField ?? ""
                            );
                        }
                    })
                );
                const visibleColumns = this.columnPreferences
                    .map(column => {
                        if (column.rollup) {
                            return {
                                name: column.fieldName,
                                rollupCalculation: column.rollupCalculation,
                                rollup: column.rollup,
                                text: this.getRollupColumnNameAlignedWithBacklog(column.rollupCalculation),
                                columnIndex: column.columnIndex,
                                width: column.columnWidth
                            } as IBacklogColumn;
                        } else {
                            return content.columns.find(col => col.name === column.fieldName);
                        }
                    })
                    .filter((item): item is IBacklogColumn => !!item);

                return {
                    allRows: collections.allRows,
                    columnMap,
                    itemProvider: itemProvider,
                    rows: rows,
                    visibleColumns: content.columns,
                    rollupColumns: visibleColumns
                };
            }

            return undefined;
        } finally {
            performanceService.addSplitTiming("Boards.buildBacklogTree.end");
        }
    }

    private getWorkItemOrder = (needToSetOrderValue: boolean, orderNumber: number): number | undefined => {
        return this.shouldShowOrderValues && needToSetOrderValue ? orderNumber : undefined;
    };

    private clearCache = (): void => {
        this.backlogContent = undefined;
        this.allIdsToRowsMap.clear();
        if (this.treeItemProvider) {
            this.treeItemProvider.value = undefined;
        }
        this.backlogWorkItemTypeNames = [];
        this.workItemIdsSet = undefined;
    };

    public updateDisplayState() {
        const rowCount = this.backlogContent?.payload?.rows?.length ?? 0;
        if (rowCount > 0) {
            this.displayState.value = DisplayState.HasData;
        } else {
            this.displayState.value = DisplayState.ZeroData;
        }
    }

    private async ensureBacklogContents(
        getData: (options: any) => Promise<ICommonBacklogData | ExceptionInfoResult>,
        options?: { ignoreCache: boolean; keepOpen?: boolean }
    ) {
        if (options?.ignoreCache || !this.backlogContent) {
            if (!options?.keepOpen) {
                this.displayState.value = DisplayState.Loading;
            }
            const [result, teamSettings] = await Promise.all([getData(options), this.teamSettingsService.getTeamSettings()]);
            this.setTeamAreaNodes(teamSettings);

            if (isExceptionInfoResult(result)) {
                this.backlogContent = undefined;
                this.exceptionInfo.value = result.exceptionInfo;
            } else {
                const isFilterEmpty = () => this.filter.statesAreEqual(this.filter.getDefaultState(), this.filter.getState());

                this.exceptionInfo.value = undefined;
                if (result?.initialBacklogFilterJson && isFilterEmpty()) {
                    this.filter.setState(mapIFilterState(JSON.parse(result.initialBacklogFilterJson)));
                }

                if (!isFilterEmpty()) {
                    this.showFilterHeader.value = true;
                }
                this.backlogContent = result.backlogQueryResults;
                this.backlogLevelId = result.backlogLevelId;
                this.backlogWiql.value = result.backlogWiql;
                for (const backlogWorkItemType of result.backlogWorkItemTypeNames) {
                    if (!this.backlogWorkItemTypeNames.some(backlogWorkItemTypeName => backlogWorkItemTypeName === backlogWorkItemType)) {
                        this.backlogWorkItemTypeNames.push(backlogWorkItemType);
                    }
                }
                this.columnPreferences = result.columnPreferences ?? [];
            }

            if (!this.backlogTreeLoadingOptimizationIsEnabled) {
                this.updateDisplayState();
            }
        }
    }

    public getWorkitemTypesMinDepth(workItemTypes: string[]): number {
        let minDepth = 0;
        for (const workItemType of workItemTypes) {
            const workItemTypeDepth = this.getWorkItemTypeDepth(workItemType);
            if (minDepth === 0 || workItemTypeDepth < minDepth) {
                minDepth = workItemTypeDepth;
            }
        }

        const currentBacklogLevelDepth = this.getCurrentBacklogLevelDepth();
        if (minDepth && currentBacklogLevelDepth) {
            minDepth = minDepth - currentBacklogLevelDepth;
        }

        return minDepth;
    }

    private getCurrentBacklogLevelDepth(): number | undefined {
        const currentLevel = this.getBacklogLevelId();
        const currentBacklogLevelIndex = this.allBacklogLevels.findIndex(level => level.id === currentLevel || level.name === currentLevel);
        const firstElementLevelIndex = this.getLevelIndexOfItemOrFirst();
        if (firstElementLevelIndex != undefined && currentBacklogLevelIndex > firstElementLevelIndex) {
            //show parents option can show Epics for stories backlog level
            return firstElementLevelIndex;
        }
        return currentBacklogLevelIndex;
    }

    private getWorkItemTypeDepth(workItemType: string): number {
        return this.allBacklogLevels.findIndex(level => level.workItemTypes.find(type => type.name === workItemType));
    }

    // Get general backlog work item type children for a specific work item type
    private async getBacklogChildrenFromType(
        workItemType: string,
        workItemTypeColorAndIcon: WorkItemTypeColorAndIcon[]
    ): Promise<WorkItemTypeColorAndIcon[]> {
        await this.getAllBacklogLevels();
        // find which backlog this item is in
        const currentBacklogLevelRank = this.allBacklogLevels.find(item => item.workItemTypes.find(element => element.name === workItemType))?.rank;
        if (currentBacklogLevelRank) {
            const workItemTypes = this.allBacklogLevels.find(backlog => backlog.rank < currentBacklogLevelRank)?.workItemTypes;
            if (workItemTypes) {
                const typesAndColors: WorkItemTypeColorAndIcon[] = [];
                for (let j = 0; j < workItemTypes.length; j++) {
                    const foundWorkItemTypeColorAndIcon = workItemTypeColorAndIcon.find(
                        item => !item.isDisabled && item.workItemTypeName === workItemTypes[j].name
                    );
                    if (foundWorkItemTypeColorAndIcon) {
                        typesAndColors.push(foundWorkItemTypeColorAndIcon);
                    }
                }
                return typesAndColors;
            }
        }
        return [];
    }

    private getBacklogLevelKey() {
        let backlogLevelKey: string | undefined = undefined;

        if (this.backlogLevelKeyFixEnabled) {
            const backogLevel = this.allBacklogLevels.find(level => level.id === this.backlogLevelId);
            backlogLevelKey = backogLevel?.type === BacklogType.Requirement ? BacklogColumnSettings.IssuesKey : this.backlogLevelId;
        } else {
            switch (this.backlogLevelId) {
                case BacklogColumnSettings.BacklogItems:
                case BacklogColumnSettings.Issues:
                case BacklogColumnSettings.Requirements:
                    backlogLevelKey = BacklogColumnSettings.IssuesKey;
                    break;
                default:
                    backlogLevelKey = this.backlogLevelId; // Custom levels will use this Id
            }
        }

        return backlogLevelKey;
    }

    // Get child work item types for all retrieved items on backlog
    private async getChildWorkItemTypes(
        columnMap: any,
        rows: any[],
        workItemTypeColorAndIcon: WorkItemTypeColorAndIcon[]
    ): Promise<{ [workItemId: number]: WorkItemTypeColorAndIcon[] }> {
        let childWorkItemTypeMap: {
            [workItemId: number]: WorkItemTypeColorAndIcon[];
        } = {};

        const typeColumn = columnMap[CoreFieldRefNames.WorkItemType];
        const idColumn = columnMap[CoreFieldRefNames.Id];

        await this.getAllBacklogLevels();

        await Promise.all(
            rows.map(async (value: any, index: number) => {
                const workItemType = rows[index][typeColumn];
                const id = rows[index][idColumn];
                childWorkItemTypeMap[id] = await this.getBacklogChildrenFromType(workItemType, workItemTypeColorAndIcon);
            })
        );

        return childWorkItemTypeMap;
    }

    private getCorrectIdentityData = async (allRows: { [id: number]: IBacklogTreeRow }) => {
        // Get all unique assignedTo values
        const assignedToValues: IBacklogTreeRow[] = [...new Set(Object.values(allRows).filter(row => row.value.coreColumnMap[CoreFieldRefNames.AssignedTo]))];
        if (!this.backlogTreeLoadingOptimizationIsEnabled) {
            //find single workItem is for each unique assignedTo value 
            const ids = assignedToValues.map(id => Number(id.value.coreColumnMap[CoreFieldRefNames.Id]));

            // Retrieving work item data caches new identity information
            const workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
            return workItemModelService.getWorkItems(ids);
        } else if (!this.backlogDelayLoadIdentitiesIsEnabled) {
            await this._beginResolveIdentityFields(assignedToValues);
        }
    };


    private _beginResolveIdentityFields = async (
        rows: IBacklogTreeRow[],
    ): Promise<void> => {
        const displayNames = Array.from(
            new Set(rows.map(row => row.value.coreColumnMap[CoreFieldRefNames.AssignedTo] as string))
        )
            .map(assignedToValue => parseUniquefiedIdentityName(assignedToValue).displayName)
            .join(";");

        if (!displayNames) {
            return;
        }

        try {
            const identityService = this.pageContext.getService<IWorkItemImportIdentityService>(WorkItemIdentityServiceName);

            const identities = await identityService.searchIdentitiesAsync(
                displayNames,
                undefined, // identityTypes
                undefined, // operationScopes
                undefined, // queryTypeHint
                undefined, // options
                undefined, // filterIdentity
                this.backlogTreeLoadingOptimizationIsEnabled,
                this.backlogDelayLoadIdentitiesIsEnabled);
            if (identities?.length) {
                identities.forEach(identity => {
                    const workItemIdentityRef = convertWorkItemIdentityRefFromFieldValue(this.pageContext, identity);
                    isWorkItemIdentityRef(workItemIdentityRef); // to force add workItemIdentityRef to cache. It will be used in filter
                });
            }
        } catch (error: any) {
            this.showErrorMessage(error);
        }
        if (this.backlogDelayLoadIdentitiesIsEnabled) {
            rows.forEach(row => { row.value.isIdentityResolved = true; });
        }
    };

    private async getFieldDefinitionDisplayName(aggregationField: string): Promise<string | undefined> {
        if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)) {
            await (this.workItemFieldService as IWorkItemFieldService2).loadFields();
        } else {
            await (this.workItemFieldService as IWorkItemFieldService).getFieldsAsync();
        }

        const fieldDefinition = this.workItemFieldService.getFieldByReferenceName(aggregationField);
        return fieldDefinition?.name;
    }

    private getRollupColumnProviders(resetProviders?: boolean) {
        if (!this.columnPreferences) {
            return;
        }
        if (this.rollupProviders && !resetProviders) {
            return this.rollupProviders;
        }
        const tfsPageData = this.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        const projectId = tfsPageData?.project?.id ?? "";
        const rollupColumns = this.columnPreferences.filter(column => column.rollup);
        const rollupProviders = [];
        for (const column of rollupColumns) {
            rollupProviders.push(
                new WorkItemRollupProvider(
                    this.pageContext,
                    column.rollupCalculation,
                    projectId,
                    this.getWorkItemTypesOfBacklogLevel(column.rollupCalculation.backlogFilter)
                )
            );
        }
        this.rollupProviders = rollupProviders;
        return rollupProviders;
    }
    private async getRowRollupData(rollupProviders: WorkItemRollupProvider[], rows: ITreeItem<IBacklogTreeRow>[]) {
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (row.data.value.columnValues.length == 0) {
                break;
            }
            for (const provider of rollupProviders) {
                const rollupData = provider.getRollupData(row.data.id);
                row.data.value.columnValues.push(rollupData);
                const index = row.data.value.columnValues.length - 1;
                // sync column index for sorting purposes
                const colIndex = this.columnPreferences.findIndex(col => {
                    if (col.rollup) {
                        return isRollupEqual(col.rollupCalculation, provider.getRollupMetric());
                    }
                });
                if (colIndex && this.columnPreferences[colIndex]) {
                    this.columnPreferences[colIndex].columnIndex = index;
                }
            }
        }
        return rows;
    }

    private getQueryName = (directory: DirectoryItem | undefined) => {
        const state = this.historyService.getState();
        let currentLevelName = state["backlogLevel"] ?? state["iteration"];
        currentLevelName = currentLevelName.slice(currentLevelName.lastIndexOf("/") + 1);
        const teamName = directory?.name;
        if (teamName) {
            return `${teamName} - ${currentLevelName}`;
        }
        return "";
    };

    private getQuerySaveDialog = (directory: DirectoryItem | undefined, wiql: string, onDismiss: () => void) => {
        const item = {
            wiql: wiql,
            name: this.getQueryName(directory),
            isFolder: false
        } as QueryItem;

        const onSave = (queryId: string) => {
            this.queryUrl = getQueryUrl(this.pageContext, queryId);
            this.showQueryHeader.value = true;
        };

        return <QuerySaveDialog mode={QuerySaveDialogMode.NewQuery} queryItem={item} onDismiss={onDismiss} onSave={onSave} />;
    };

    private handleCreateQueryAction = () => {
        const currentDirectoryService = this.pageContext.getService<ICurrentDirectoryService>("ICurrentDirectoryService");
        this.openDialog(onDismiss => this.getQuerySaveDialog(currentDirectoryService.getCurrentDirectory().value, this.backlogWiql.value, onDismiss));
    };

    private onClickNewWorkitemBtn = (e: React.MouseEvent<HTMLElement>) => {
        this.newItemCalloutAnchor.value = e.currentTarget;
    };

    private openDialog(callout: (dismisss: () => void) => JSX.Element): void {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => callout(onDismiss));
    }

    private parseBacklogItemRowData = async (
        row: RawRowData[],
        columns: string[],
        workItemStateAndColorDict: { [workItemType: string]: WorkItemStateAndColor[] },
        workItemTypeColorAndIcons: WorkItemTypeColorAndIcon[]
    ) => {
        const systemIdIndex = columns.indexOf(CoreFieldRefNames.Id);
        const coreColumnMap = createCoreColumnMap(columns, row);

        const { workItemState, workItemTags, workItemType, workItemProject } = getCoreColumnValues(coreColumnMap);

        let newState: WorkItemStateAndColor | undefined;
        const newTags: string[] = [];
        let newType: WorkItemTypeColorAndIcon | undefined;

        workItemTags.forEach(tag => {
            if (tag.length > 0) {
                newTags.push(tag);
            }
        });

        // workItemState and workItemType will never be an empty string *unless* the row needs to be paged in later
        // therefore, checking for one being valid is equivalent to checking both
        if (workItemType) {
            newState = workItemStateAndColorDict[workItemType]?.find(itemState => equals(itemState.name, workItemState));
            newType = workItemTypeColorAndIcons?.find(tci => equals(tci.workItemTypeName, workItemType));

            if (!newState) {
                const workItemStateColorMetadataService = this.pageContext.getService<IWorkItemStateColorMetadataService>(
                    "work-item-state-color-metadata-service"
                );
                const workItemStateAndColor = await workItemStateColorMetadataService.getWorkItemStateColor(
                    workItemProject,
                    workItemType,
                    workItemState
                );
                if (workItemStateAndColorDict[workItemType]) {
                    workItemStateAndColorDict[workItemType].push(workItemStateAndColor);
                }
                workItemStateAndColorDict[workItemType] = [workItemStateAndColor];

                newState = workItemStateAndColor;
            }

            if (!newType && workItemTypeColorAndIcons) {
                const workItemTypeIconMetadataService = this.pageContext.getService<IWorkItemTypeIconMetadataService>(
                    "work-item-type-icon-metadata-service"
                );
                const workItemIcon = await workItemTypeIconMetadataService.getWorkItemIcon(workItemProject, workItemType);
                workItemTypeColorAndIcons.push(workItemIcon);

                newType = workItemIcon;
            }
        }

        const parentId = coreColumnMap[CoreFieldRefNames.Parent] as number;
        if (parentId && !this.parentWorkItems.get(parentId)) {
            this.parentWorkItems.set(parentId, new ObservableValue(undefined));
        }

        const id = row[systemIdIndex] as number;
        const backlogItemUpdates = {
            columnValues: row,
            coreColumnMap: coreColumnMap,
            workItemStateAndColor: newState,
            workItemTypeColorAndIcon: newType
        };

        return {
            id: id,
            backlogItemUpdates: backlogItemUpdates
        };
    };

    private setParents = async (parentWorkItemIds: number[]) => {
        const workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        try {
            //we don't need to receive unparented element from backend
            parentWorkItemIds = parentWorkItemIds.filter(x => x >= 0);

            //check if unparented element was the only one
            if (parentWorkItemIds && parentWorkItemIds.length === 0) {
                return;
            }

            const parents = await workItemModelService.getWorkItems(parentWorkItemIds);
            for (const parent of parents) {
                const parentItem = this.parentWorkItems.get(parent.id);
                if (parentItem) {
                    parentItem.value = parent;
                }
            }
        } catch (error) {
            // getting parents can fail when there is same category hierarchy. we should ignore the error
        }
    };

    private setTeamAreaNodes = (teamSettings: ITeamSettings | undefined) => {
        if (!teamSettings) {
            return;
        }
        teamSettings.teamFieldValues.forEach(node => this.teamAreaNodesById.set(node.value, node));
    };

    private getExpandedItemIds(): Set<number> {
        const expandedItemIds: Set<number> = new Set();
        this.backlogTree.value?.itemProvider.value.forEach(item => {
            if (item.underlyingItem.expanded) {
                expandedItemIds.add(item.underlyingItem.data.id);
            }
        });
        return expandedItemIds;
    }

    private setUnparentedFolderItems(
        visibleRows: IBacklogTreeRow[],
        mapRowToTreeItem: (row: IBacklogTreeRow, isExpanded: boolean) => ITreeItem<IBacklogTreeRow>
    ) {
        const levelId = this.getBacklogLevelId();
        const backlogLevels = this.allBacklogLevels;
        const targetBacklogLevelName = backlogLevels.find(level => level.id === levelId)?.name;
        const showParents = this.historyService.getState().showParents === "true";

        const rows: ITreeItem<IBacklogTreeRow>[] = [];
        let currentUnparentedFolder: IBacklogTreeRow | undefined = undefined;
        let currentChildItems: IBacklogTreeRow[] = [];
        const finalItemIndex = visibleRows.length - 1;
        let backlogLevelIndex = showParents ? this.getStartLevelIndex(targetBacklogLevelName!, visibleRows) : 1;
        let parentFolder: ITreeItem<IBacklogTreeRow> | undefined = undefined;

        const addEmptyItem = () => {
            if (!currentUnparentedFolder) {
                return;
            }

            const expandedItemIds = this.getExpandedItemIds();

            const emptyBacklogItem: ITreeItem<IBacklogTreeRow> = {
                expanded: !(expandedItemIds.size > 0 && !expandedItemIds.has(currentUnparentedFolder.id)),
                childItems: currentChildItems.map(row => mapRowToTreeItem(row, false)),
                data: currentUnparentedFolder
            };
            emptyBacklogItem.data.children = currentChildItems;
            // find backlog name to display on folder items - unparented folders are always ordered from top of hierarchy to bottom
            if (this.hub === HubType.Backlogs) {
                emptyBacklogItem.data.value.backlogLevel = backlogLevels[backlogLevelIndex].name;
            } else {
                emptyBacklogItem.data.value.backlogLevel = "";
            }
            backlogLevelIndex++;

            if (!parentFolder || this.hub === HubType.Sprints) {
                rows.push(emptyBacklogItem);
            } else {
                parentFolder.childItems?.push(emptyBacklogItem);
            }

            parentFolder = emptyBacklogItem;
            currentChildItems = [];
        };

        // rows are organized based on unparented negative indexes
        // split up child items here based on unparented folders
        for (let i = 0; i < visibleRows.length; i++) {
            const item = visibleRows[i];

            if (item.id >= 0) {
                if (currentUnparentedFolder) {
                    const itemLevel = backlogLevels.find(level =>
                        level.workItemTypes.some(itemType => itemType.name === item.value.workItemTypeColorAndIcon?.workItemTypeName)
                    );

                    if (this.hub === HubType.Sprints && itemLevel?.id === BacklogColumnSettings.Requirements) {
                        addEmptyItem();
                        currentUnparentedFolder = undefined;
                        rows.push(mapRowToTreeItem(item, true));
                    } else {
                        currentChildItems.push(item);
                    }
                } else {
                    rows.push(mapRowToTreeItem(item, true));
                }
            }

            if (currentUnparentedFolder && (item.id <= 0 || i == finalItemIndex)) {
                addEmptyItem();
                currentUnparentedFolder = item;
            } else if (!currentUnparentedFolder && item.id <= 0) {
                currentUnparentedFolder = item;
            }
        }
        return rows;
    }

    private updateBacklogTree = async (
        getData: (options: any) => Promise<ICommonBacklogData | ExceptionInfoResult>,
        options?: { ignoreCache: boolean; updateFiltersOrOrder?: boolean; keepOpen?: boolean; filterManager?: FilterManager }
    ): Promise<void> => {
        if (options?.updateFiltersOrOrder || options?.ignoreCache || (!this.backlogTree.value && !this.buildBacklogTreePromise)) {
            this.buildBacklogTreePromise = this.buildBacklogTree(getData, options);

            this.backlogTree.value = await this.buildBacklogTreePromise;
            if (this.backlogTreeLoadingOptimizationIsEnabled) {
                // we need to hide the hide loading indicator after the tree is built
                this.updateDisplayState();
            }
            if (options?.updateFiltersOrOrder) {
                options?.filterManager && options.filterManager.dataUpdated();
                this.filter.setState(this.filter.getState());
            }
            this.buildBacklogTreePromise = undefined;
        }
    };

    private updateRollupDataForNewItem(id: number, columnValues: RawRowData[]): RawRowData[] {
        const rollupProviders = this.getRollupColumnProviders();
        if (rollupProviders) {
            rollupProviders.forEach(provider => {
                const rollupData = provider.getRollupData(id);
                columnValues.push(rollupData);
            });
        }

        return columnValues;
    }

    public getOrAddUnparentedFolder(): ITreeItem<IBacklogTreeRow> | undefined {
        const levelId = this.getBacklogLevelId();
        const levels = this.allBacklogLevels;
        const targetBacklogLevelName = levels.find(level => level.id === levelId)?.name;
        const firstItemLevelName = this.getBacklogLevelNameOfFirstItem();
        const firstVisibleLevelName = levels.find(level => !level.isHidden)?.name;

        let rows: ITreeItem<IBacklogTreeRow>[] | undefined = this.backlogTree.value?.rows;
        let hasFolder = false;
        let lastParent: ITreeItem<IBacklogTreeRow> | undefined = undefined;
        let levelIndex = this.getStartLevelIndex(targetBacklogLevelName!);
        let backlogLevelName = levels[levelIndex].name;
        let rootLevelName = backlogLevelName;

        // If true it means that we are at the root level and we don't need to create any new folders
        if (
            (!firstItemLevelName || targetBacklogLevelName === firstItemLevelName) &&
            targetBacklogLevelName === rootLevelName &&
            targetBacklogLevelName === firstVisibleLevelName
        ) {
            return;
        }

        while (rows) {
            rows.forEach(row => {
                if (row.data.value.backlogLevel) {
                    row.expanded = true;
                    hasFolder = true;

                    if (row.data.value.backlogLevel === targetBacklogLevelName) {
                        return;
                    }

                    lastParent = row;
                    rows = row.childItems;
                }
            });

            // If there is no folder in rows and it's not the target backlog level we need to create a new folder
            if (!hasFolder) {
                const id = levelIndex - levels.length;
                const unparentedFolder = this.createTempUnparentedFolder(id, backlogLevelName);
                this.backlogTree.value?.itemProvider.add(unparentedFolder, lastParent, rows?.[rows.length - 1]);

                if (targetBacklogLevelName === backlogLevelName) {
                    return unparentedFolder;
                }
                rows = unparentedFolder.childItems;
                lastParent = unparentedFolder;
            } else {
                const folderIndex = rows?.findIndex(row => row.data.value.backlogLevel === targetBacklogLevelName);
                if (folderIndex > -1) {
                    lastParent = rows[folderIndex];
                }
            }

            levelIndex++;
            hasFolder = false;

            if (levelIndex < levels.length) {
                backlogLevelName = levels[levelIndex].name;
            } else {
                break;
            }
        }

        return lastParent;
    }

    public getAllVisibleLevels(): BacklogLevelConfiguration[] {
        return this.allBacklogLevels.filter(level => !level.isHidden);
    }

    public isParentInSameProject(workItemId: number, parentId: number): boolean {
        const index = this.backlogContent?.targetIds.findIndex(id => id === workItemId);
        if (index === undefined || index === -1) {
            return false;
        }

        return this.backlogContent?.realParentIds[index] === parentId;
    }

    private getStartLevelIndex(currentLevelName: string, allVisibleRows?: IBacklogTreeRow[]): number {
        // To correctly build the parent tree we need to find a backlog level of the first item
        const indexOfFirstItem = allVisibleRows ? this.getLevelIndexOfItemOrFirst(allVisibleRows[0]) : this.getLevelIndexOfItemOrFirst();
        if (indexOfFirstItem !== undefined) {
            if (this.allBacklogLevels[indexOfFirstItem].name === currentLevelName) {
                return indexOfFirstItem;
            }

            return indexOfFirstItem + 1;
        }

        // Check if we don't have hidden items and if it so we need to return 0 for the root level and 1 for others
        const firstVisibleLevelIndex = this.allBacklogLevels.findIndex(level => !level.isHidden);
        if (firstVisibleLevelIndex === 0) {
            return this.allBacklogLevels[firstVisibleLevelIndex].name === currentLevelName ? 0 : 1;
        }

        // If we have hidden items we need to check if there are folders in rows and calculate them
        // to correctly offset the root element index relative to the current one
        let foldersNumber = 0;
        if (allVisibleRows) {
            foldersNumber = allVisibleRows.reduce((prev, curr) => (curr.id < 0 ? ++prev : prev), 0);
        } else {
            this.allIdsToRowsMap.forEach((_, id) => {
                if (id < 0) {
                    foldersNumber++;
                }
            });
        }

        if (foldersNumber > 1) {
            let index = this.allBacklogLevels.findIndex(level => level.name === currentLevelName);
            return index - foldersNumber + 1;
        }

        // If there are not folders in rows then we need to offset the level relative to the first visible item
        if (firstVisibleLevelIndex !== -1) {
            const index = this.allBacklogLevels.findIndex(level => level.name === currentLevelName);
            return firstVisibleLevelIndex === index ? firstVisibleLevelIndex : firstVisibleLevelIndex + 1;
        }

        return 1;
    }

    private getLevelIndexOfItemOrFirst(item?: IBacklogTreeRow): number | undefined {
        const firstItemType = !item ? this.getItemType(this.backlogTree.value?.rows[0]?.data) : this.getItemType(item);
        const index = this.allBacklogLevels.findIndex(level => level.defaultWorkItemType.name === firstItemType);
        if (firstItemType && index !== -1) {
            return index;
        }
        return undefined;
    }

    private getBacklogLevelNameOfFirstItem(): string | undefined {
        const firstItemTypeName = this.getItemType(this.getFirstItem());
        if (firstItemTypeName) {
            return this.allBacklogLevels.find(level => level.defaultWorkItemType.name === firstItemTypeName)?.name;
        }
    }

    private getItemType(item: IBacklogTreeRow | undefined): string | undefined {
        return item?.value.coreColumnMap[CoreFieldRefNames.WorkItemType]?.toString();
    }

    private getFirstItem(): IBacklogTreeRow | undefined {
        const firstItemId = this.backlogContent?.targetIds.find(id => id > 0);
        if (firstItemId) {
            const item = this.getAllIdsToRowsMap().get(firstItemId);
            return item;
        }

        return undefined;
    }

    private createTempUnparentedFolder(id: number, backlogLevel?: string): ITreeItem<IBacklogTreeRow> {
        const data = new ObservableBacklogItem(id, undefined, {
            childWorkItemTypes: [],
            columnValues: [],
            coreColumnMap: {},
            isValid: true,
            workItemUrl: "",
            backlogLevel: backlogLevel
        });

        return { expanded: true, childItems: [], data };
    }
}

function createCoreColumnMap(columnNames: string[], row: RawRowData[]): { [columnName: string]: RawRowData } {
    return row.reduce((map: { [columnId: string]: RawRowData }, rawColumnValue: RawRowData, index: number) => {
        map[columnNames[index]] = rawColumnValue;
        return map;
    }, {});
}

function getCoreColumnValues(coreColumnMap: { [columnName: string]: RawRowData }) {
    return {
        workItemState: (coreColumnMap[CoreFieldRefNames.State] ?? "") as string,
        workItemTags: ((coreColumnMap[CoreFieldRefNames.Tags] ?? "") as string).split(";"),
        workItemType: (coreColumnMap[CoreFieldRefNames.WorkItemType] ?? "") as string,
        workItemProject: (coreColumnMap[CoreFieldRefNames.TeamProject] ?? "") as string
    };
}

export function isExceptionInfoResult(x: any): x is ExceptionInfoResult {
    return (x as ExceptionInfoResult).exceptionInfo ? true : false;
}

// until Array.flat/flatMap is supported
function flatten<T>(multiArray: T[][]): T[] {
    const result: T[] = [];
    for (const singleArray of multiArray) {
        result.push(...singleArray);
    }

    return result;
}

Services.add("ICommonBacklogContentService", { serviceFactory: CommonBacklogContentService });
